import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link} from 'react-router-dom';
import { faArrowLeftLong, faChevronDown, faChevronRight, faDownload, faImage, faRobot } from '@fortawesome/free-solid-svg-icons';
import { useIsMobile } from '../../hooks/useIsMobile';
import { DesktopOnlyNotice } from '../../components/ui/DesktopOnlyNotice';
import {SettingsPanel} from '../../components/app/ImageStudio/SettingsSidebar';
import ImageProps, { ExtendPadding, ImageLayerProps, LayerAreaSelection, LayerProps, OpenExtandImageState, OverlayRect, ProjectSaveSnapshot, ProjectSaveSnapshotWithVariations, SettingsPanelAiEditProps, SettingsPanelEditInputProps } from '../../types/imageStudio/images';
import EffectThemeContainer from '../../components/effectTheme/effectThemeContainer';
import EffectThemeButton from "../../components/effectTheme/effectThemeButtons";
import { cropCanvas, rgbToHex, rotateCanvas } from '../../utils/images/editFunctions';
import { BasicImageToolKit} from '../../components/app/ImageStudio/Toolbar';
import { SengiColors } from '../../utils/SengiColors';
import { OverlayPortal } from '../../components/app/ImageStudio/OverlayPortal';
import { useLayerFunctionAndSidebar } from '../../components/app/ImageStudio/LayerComponent';
import { ContextProps } from '../../types/imageStudio/contextMenu'; 
import { canvasToFile, fileToImageProps, loadImage } from '../../utils/images/imagesFunctions';
import downloadImageInFormat, { aspectRatioList, composeImageWithLayers, downloadScaleTypes, ExportFormat } from '../../utils/images/imageConvertFormats';
import { applyLayerResizeContentSync, getLayerPositionKeepingLocalPoint, layerLocalPointToCanvasPoint, mergeLayerAndGetLayerWithSelectionIds, renderLayerToCanvas, sortLayersByOrderAscending } from '../../utils/images/layerFunctions';
import { AiLayerVariationGenerationProps, aiModelList, qualityList, samplerList, schedulerList } from '../../types/ai/api';
import { ColorPickTarget, OverlayCorner } from '../../types/imageStudio/overlay';
import { ContextMenu, ContextMenuEmptyBase } from '../../components/app/ImageStudio/ContextMenu';
import { InfoPopoverButton } from '../../components/app/ImageStudio/InfoButton';
import { ControllInfoElement, WorkLoadInfoElement } from '../../utils/aiModels/infotexts';
import sengiQuestionRight from "../../assets/Sengi_fragen_1.svg";
import sengiControll1 from "../../assets/sengi_controll_3.png";
import sengiControll2 from "../../assets/sengi_control_5.png";
import { stylesList } from '../../utils/images/imageStylesList';
import AILogo from '../../components/app/icons/AILogo';
import { AiSelectionContext, stateIconDone, stateIconError, stateIconSend, stateIconWaiting } from '../../types/ai/states';
import { BasicToolkitMode } from '../../types/imageStudio/toolbar';
import { ImageStudioGallery } from '../../components/app/ImageStudio/ImageGallery';
import Cookies from 'js-cookie';
import { getAllThumbnails, loadProjectPngFromThumbnail, openImageProject, readHistoryFiles, stripKnownImageStudioExtensions, updateCookieHistory, uploadProjectData } from '../../utils/aiModels/apiImageStudio';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { ensureOverlayInBounds, getKeyboardCornerFromCode, getKeyboardStep } from '../../utils/images/overlayFunctions';
import { DownloadFailedToast, DownloadStartToast, handleShowToast } from '../../components/toasts/toasts';

declare global {
    interface Window {
        EyeDropper?: new () => {
            open: () => Promise<{ sRGBHex: string }>;
        };
    }
};

export const ImageStudio: React.FC = () => {
    const { t } = useTranslation('imageStudio');
    const isMobile = useIsMobile();
    const MAIN_DATA_PATH_IMAGES = 'imageStudio/all';
    const ThumbnailSize = 200;
    const autosaveTimer = 15000;
    const History_Cookie_name = 'imageStudioThumbnailHistory'; // cookie to store thumbnail paths
    const downloadStartElementID = 'ToastDownloadStart';
    const downloadFailedElementID = 'ToastDownloadFailed';
    const [generatedImages, setGeneratedImages] = useState<ImageProps[]>([]);
    const [selectedImage, setSelectedImage] = useState<ImageProps | null>(null);
    const [baseImageVariations, setBaseImageVariarions] = useState<ImageLayerProps[]>([]);
    const [hoverPreview, setHoverPreview] = useState<ImageProps | null>(null);
    const [isBaseImageVariationSelected, setIsBaseImageVariationSelected] = useState(false);
    const [selectedLayerIDs, setSelectedLayerIDs] = useState<number[]>([]);
    const [selectedLayer, setSelectedLayer] = useState<LayerProps | null>(null);
    const [inputEditFromSidebar, setInputEditFromSidebar] = useState<SettingsPanelEditInputProps | null>(null);
    const [inputAiEditFromSidebar, setInputAiEditFromSidebar] = useState<SettingsPanelAiEditProps | null>(null);
    const [aiEditReference, setAiEditReference] = useState<AiLayerVariationGenerationProps | null>(null);
    const [createdAt, setCreatedAt] = useState<Date|null>(null);
    const [hoverPreviewSource, setHoverPreviewSource] = useState<"gallery" | "generation" | "variation" | null>(null);
    //gallery
    const [showGallery, setShowGallery] = useState<boolean>(false);
    const [folderActive, setFolderActive] = useState<'Current'|'History'|'All'>('Current');
    const [currentImages,setCurrentImages] = useState<File[]>([]); //only contains thumbnails
    const [currentImageObjects, setCurrentImageObjects] = useState<ProjectSaveSnapshotWithVariations[]>([]);
    const [historyOfUsedImages,setHistoryOfUsedImages] = useState<File[]>([]); // read thumbnails from cookie
    const [allImages, setAllImages] = useState<File[]>([]); // read thumbnails from data storage
    const [loadProjectDataActive, setLoadProjectDataActive] = useState(false);
    //edit
    const [historyOfEditedImages, setHistoryOfEditedImages] = useState<SettingsPanelEditInputProps[]>([]);
    const [overlayTarget, setOverlayTarget] = useState<"image" | "layer">("image");
    const [imageChangeSource, setImageChangeSource] = useState<"external" | "edit">("external");
    const [selectedVariationIndex, setSelectedVariationIndex] = useState<number | null>(null);
    const [autosaveActive,setAutoSaveActive] = useState<boolean>(false);
    const [projectDirtyRevision, setProjectDirtyRevision] = useState(0);

    //references
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const canvasDrawTokenRef = useRef(0); //updating hover preview ref
    const canvasWrapperRef = useRef<HTMLDivElement>(null);
    const canvasViewportRef = useRef<HTMLDivElement>(null);
    const imageStageTransformRef = useRef<HTMLDivElement>(null);
    const canvasContextAreaRef = useRef<HTMLDivElement>(null);
    const loadProjectDataActiveRef = useRef(false);
    const autosaveTimerRef = useRef<number | null>(null);
    const saveProjectRunningRef = useRef(false);
    const pendingProjectSaveRef = useRef<ProjectSaveSnapshot | null>(null);
    const projectDirtyRef = useRef(false);
    const lastLayerDirtyRef = useRef<LayerProps[] | null>(null);
    const keyboardResizeCornerRef = useRef<OverlayCorner>("se");
    const keyboardResizeActiveRef = useRef(false);
    const keyboardResizeKeyCodeRef = useRef<string | null>(null);
    
    const getCanvasDisplayRect = () => {
        const wrapper = canvasWrapperRef.current;
        const canvas = canvasRef?.current;
        if (!wrapper || !canvas) return null;

        const wrapperRect = wrapper.getBoundingClientRect();
        const canvasRect = canvas.getBoundingClientRect();

        return {
            left: canvasRect.left - wrapperRect.left,
            top: canvasRect.top - wrapperRect.top,
            width: canvasRect.width,
            height: canvasRect.height,
        };
    };
    const editingTextValueRef = useRef("");
    const pendingShortcutRef = useRef<"flip" | null>(null);
    const middleMouseDragRef = useRef<{
        mode: "canvas" | "layers";
        startX: number;
        startY: number;
        startPanX: number;
        startPanY: number;
        startLayers: LayerProps[];
    } | null>(null);
    const [canvasDisplayRect, setCanvasDisplayRect] = useState<{
        left: number;
        top: number;
        width: number;
        height: number;
    } | null>(null);

    const updateCanvasDisplayRect = () => {
        setCanvasDisplayRect(getCanvasDisplayRect());
    };

    //Toolkit
    const TOOLBAR_BREAKPOINT_WIDTH = 2300;
    const toolkitReservedHeight = 170;
    const [windowSizeSmall, setWindowSizeSmall] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.innerWidth <= TOOLBAR_BREAKPOINT_WIDTH;
    });
    const [isPickingTransparencyColor, setIsPickingTransparencyColor] = useState(false);
    const [isPickingTransparencyTransitionColor, setIsPickingTransparencyTransitionColor] = useState(false);
    const [isPickingDrawingColor, setIsPickingDrawingColor] = useState(false);
    const [selectedBrushSize, setSelectedBrushSize] = useState(10);
    const [selectedBrushHardness, setSelectedBrushHardness] = useState(100);
    const [selectedBrushOpacity, setSelectedBrushOpacity] = useState(100);
    const [selectedDrawingTool, setSelectedDrawingTool] = useState<string>("");

    const [isPickingTextColor, setIsPickingTextColor] = useState(false);
    const [isPickingBackgroundColor, setIsPickingBackgroundColor] = useState(false);
    const [isPickingBorderColor, setIsPickingBorderColor] = useState(false);

    const [pickedTransparencyColor, setPickedTransparencyColor] = useState("#ffffff");
    const [pickedTransparencyTransistionColor, setPickedTransparencyTransitionColor] = useState("#ffffff");
    const [pickedDrawingColor, setPickedDrawingColor] = useState(SengiColors.sng_white);
    const [pickedTextColor, setPickedTextColor] = useState(SengiColors.sng_white);
    const [pickedBorderColor, setPickedBorderColor] = useState(SengiColors.sng_white);
    const [pickedBackgroundColor, setPickedBackgroundColor] = useState(SengiColors.sng_white);

    const transparencyTileSize = 16;
    const downloadFormats: string[] = ["jpg", "png", "svg", "pdf", "gif", "tif"];
    const [activeToolkitMode, setActiveToolkitMode] = useState<BasicToolkitMode | null>("pointer");
    const [coloPreviewList, setColoPreviewList] = useState([SengiColors.sng_white, SengiColors.sng_black, SengiColors.sng_disabled, SengiColors.sng_error, SengiColors.sng_warning, SengiColors.sng_success, SengiColors.sng_info, SengiColors.sng_lavendel]);
    const [toolkitAction, setToolkitAction] = useState<{ type: string; ts: number } | null>(null);
    const [isRotatePreviewActive, setIsRotatePreviewActive] = useState(false);
    const [rotationDraft, setRotationDraft] = useState(0);
    const [cropMode, setCropMode] = useState(false);
    const [rotateMode, setRotateMode] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    //expand
    const ZERO_EXTEND_PADDING: ExtendPadding = {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
    };
    const EXPAND_PREVIEW_RESERVED_WIDTH = 280;
    const EXPAND_PREVIEW_RESERVED_HEIGHT = 190;
    const EXPAND_HANDLE_SAFE_MARGIN = 80;
    const [expandPadding, setExpandPadding] = useState<ExtendPadding>(ZERO_EXTEND_PADDING);
    const expandZoomWasChangedRef = useRef(false);
    const [openExtandImage, setOpenExtandImage] = useState<OpenExtandImageState>({
        active: false,
        mode: "transparent",
        target: "base",
        padding: { top: 0, right: 0, bottom: 0, left: 0 }
    });
    //text 
    const [showExpandVariationModal, setShowExpandVariationModal] = useState(false);
    const [textIsBold, setTextIsBold] = useState(false);
    const [textIsItalic, setTextIsItalic] = useState(false);
    const [textIsUnderline, setTextIsUnderline] = useState(false);
    const [textOpacity, setTextOpacity] = useState(100);
    const [borderOpacityTextLayer, setBorderOpacityTextLayer] = useState(100);
    const [backgroundOpacityTextLayer, setBackgroundOpacityTextLayer] = useState(100);
    const [selectedTextSize, setSelectedTextSize] = useState(16);
    const [selectedFontType, setSelectedFontType] = useState("Arial");
    const [selectedBorderWidth, setSelectedBorderWidth] = useState(0);
    const [selectedBorderType, setSelectedBorderType] = useState("none");
    const [selectedTextContent, setSelectedTextContent] = useState("New Text");
    const [editingTextLayerId, setEditingTextLayerId] = useState<number | null>(null);
    const [editingTextValue, setEditingTextValue] = useState("");
    
    const [isMarqueeArea, setIsMarqueeArea] = useState(false);

    //images
    const [isPickingImageLayerBackgroundColor, setIsPickingImageLayerBackgroundColor] = useState(false);
    const [isPickingImageLayerBorderColor, setIsPickingImageLayerBorderColor] = useState(false);
    const [selectedImageLayerImage, setSelectedImageLayerImage] = useState<ImageProps | null>(null);
    const [selectedImageLayerOpacity, setSelectedImageLayerOpacity] = useState(100);
    const [selectedImageLayerBorderColor, setSelectedImageLayerBorderColor] = useState("#ffffff");
    const [selectedImageLayerBorderOpacity, setSelectedImageLayerBorderOpacity] = useState(100);
    const [selectedImageLayerBorderWidth, setSelectedImageLayerBorderWidth] = useState(0);
    const [selectedImageLayerBorderType, setSelectedImageLayerBorderType] = useState("none");
    const [selectedImageLayerBackgroundColor, setSelectedImageLayerBackgroundColor] = useState("#ffffff");
    const [selectedImageLayerBackgroundOpacity, setSelectedImageLayerBackgroundOpacity] = useState(100);

    const imageBorderStyleList = ["none", "solid", "dashed", "dotted"];
    const imageBorderWidthList = [0, 1, 2, 3, 4, 6, 8, 10];
    const fontTypesList = ["Arial", "Verdana", "Times New Roman", "Georgia", "Courier New", "Trebuchet MS"];
    const borderStyleList = ["none", "solid", "dashed", "dotted"];
    const borderWidthList = [0, 1, 2, 3, 4, 6, 8, 10];
    const textSizesList = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 64];

    //navigation and handling
    const [zoom, setZoom] = useState(1);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const panStartRef = useRef<{ x: number; y: number; startPanX: number; startPanY: number } | null>(null);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [isClosingOverlays, setIsClosingOverlays] = useState(false);
    const [contextMenu, setContextMenu] = useState<ContextProps>({
        visible: false,
        x: 0,
        y: 0
    });
    const beginProjectLoad = () => {
        if (loadProjectDataActiveRef.current) return false;

        loadProjectDataActiveRef.current = true;
        setLoadProjectDataActive(true);
        return true;
    };

    const endProjectLoad = () => {
        loadProjectDataActiveRef.current = false;
        setLoadProjectDataActive(false);
    };
    const setinitialImageLists = async() => {
        //read only thumbnails for gallery lists
        setCurrentImages([]);
        //read history from cookies
        const history = Cookies.get(History_Cookie_name);
        if(history){
            try{
                const convertedHistory = JSON.parse(history) as string[];
                const historyFiles:File[] = await readHistoryFiles(convertedHistory,MAIN_DATA_PATH_IMAGES);            
                setHistoryOfUsedImages(historyFiles);
            }
            catch{
                console.log('Unable to laod history');
            }
            
        }
        else{
            setHistoryOfUsedImages([]);
            Cookies.set(History_Cookie_name,'[]');
        }
        //read images from data storage
        try{
            let allThumbnails = await getAllThumbnails(`${MAIN_DATA_PATH_IMAGES}/`);
            setAllImages(allThumbnails);
        }
        catch{
            console.log('Unable to load data storage');
        }
    };
    const resizeSelectedLayerWithKeyboard = (
        corner: OverlayCorner,
        key: string,
        step: number
    ) => {
        if (selectedLayerIDs.length !== 1 || !selectedLayer) return;
        if (selectedLayer.hidden) return;
        if (cropMode || rotateMode || openExtandImage.active) return;

        const isLeft = corner.includes("w");
        const isRight = corner.includes("e");
        const isTop = corner.includes("n");
        const isBottom = corner.includes("s");

        let nextWidth = selectedLayer.width;
        let nextHeight = selectedLayer.height;

        if (key === "ArrowLeft") {
            if (isLeft) nextWidth += step;
            if (isRight) nextWidth -= step;
        }

        if (key === "ArrowRight") {
            if (isLeft) nextWidth -= step;
            if (isRight) nextWidth += step;
        }

        if (key === "ArrowUp") {
            if (isTop) nextHeight += step;
            if (isBottom) nextHeight -= step;
        }

        if (key === "ArrowDown") {
            if (isTop) nextHeight -= step;
            if (isBottom) nextHeight += step;
        }

        nextWidth = Math.max(20, Math.round(nextWidth));
        nextHeight = Math.max(20, Math.round(nextHeight));

        const fixedLocalPointOld = {
            x: isLeft ? selectedLayer.width : 0,
            y: isTop ? selectedLayer.height : 0
        };

        const fixedCanvasPoint = layerLocalPointToCanvasPoint(
            fixedLocalPointOld,
            selectedLayer
        );

        const fixedLocalPointNext = {
            x: isLeft ? nextWidth : 0,
            y: isTop ? nextHeight : 0
        };

        const resizedLayer = applyLayerResizeContentSync(
            selectedLayer,
            {
                ...selectedLayer,
                position: getLayerPositionKeepingLocalPoint(
                    selectedLayer,
                    nextWidth,
                    nextHeight,
                    fixedLocalPointNext,
                    fixedCanvasPoint
                ),
                width: nextWidth,
                height: nextHeight
            }
        );

        commitLayerChange(
            imageLayer.map(layer =>
                layer.layerID === selectedLayer.layerID
                    ? resizedLayer
                    : layer
            ),
            [selectedLayer.layerID]
        );
    };
    const createNewImageEntry = async(file:File,saveCurrentBeforeCreate = true): Promise<File|null> => { 
        const ownsLoadingLock = saveCurrentBeforeCreate;
        if (ownsLoadingLock && !beginProjectLoad()) return null;

        try {
            if (saveCurrentBeforeCreate) {
                clearAutosaveTimer();
                const previousSnapshot = createProjectSnapshot();
                projectDirtyRef.current = false;
                queueProjectSave(previousSnapshot,baseImageVariations);
            }
            //rename file to unique name
            const newBaseName = `${stripKnownImageStudioExtensions(file.name)}-${crypto.randomUUID()}`;
            const newName = `${newBaseName}.png`;
            const imgFile: File = new File(
                [file],
                newName,
                {
                    type: file.type || "image/png",
                    lastModified: file.lastModified
                }
            );
            const img = await fileToImageProps(imgFile);
            //file upload as new image project zip
            const newDate = new Date();
            
            const project = await uploadProjectData(img,[],MAIN_DATA_PATH_IMAGES,newDate,ThumbnailSize);
            if(!project) return null;
            //get thumbnail
            const thumbnailFile = project.thumbnail;

            setCreatedAt(newDate);
            //update current
            setCurrentImages(prev => [thumbnailFile,...prev]);
            updateCurrentList({
                image: img,
                layers: [],
                createdAt: newDate,
                baseVariarions: []
            });
            //update history
            updateCookieHistory(History_Cookie_name,thumbnailFile);
            setHistoryOfUsedImages(prev => [thumbnailFile,...prev]);
            //update all
            setAllImages(prev => [thumbnailFile,...prev]);

            setImageChangeSource("external");
            setSelectedImage(img);
            setImageLayer([]);
            projectDirtyRef.current = false;
            lastLayerDirtyRef.current = [];
            setAutoSaveActive(false);
            setHoverPreview(null);
            setSelectedLayer(null);
            setSelectedLayerIDs([]);
            setLayerAreaSelection(null);
            setBaseImageVariarions([]);
            onChangeExpandImageVariationsActive(false);
            setZoom(1);
            setPanOffset({ x: 0, y: 0 });
            return thumbnailFile;
        } catch (error) {
            console.error("createNewImageEntry failed:", error);
            return null;
        } finally {
            if (ownsLoadingLock) {
                endProjectLoad();
            }
        }
    };
    const updateImageProject = async (
        snapshot?: {
            image: ImageProps | null;
            layers: LayerProps[];
            createdAt: Date | null;
        },
        baseVariations?: ImageProps[]
    ): Promise<File | null> => {
        const imageToSave = snapshot?.image ?? selectedImage;
        const layersToSave = snapshot?.layers ?? imageLayer;
        const createdAtToSave = snapshot?.createdAt ?? createdAt ?? new Date();

        if (!imageToSave || !imageToSave.file) return null;

        try {
            const projectUpdate = await uploadProjectData(
                imageToSave,
                [...layersToSave],
                MAIN_DATA_PATH_IMAGES,
                createdAtToSave,
                ThumbnailSize
            );

            if (!projectUpdate) return null;

            const thumbnail = projectUpdate.thumbnail;
            updateCurrentList({
                image: imageToSave,
                layers: layersToSave,
                baseVariarions: baseVariations ?? [],
                createdAt: createdAtToSave
            });
            setCurrentImages(prev => {
                const index = prev.findIndex(file => file.name === thumbnail.name);
                if (index === -1) return [thumbnail, ...prev];

                const next = [...prev];
                next[index] = thumbnail;
                return next;
            });

            updateCookieHistory(History_Cookie_name, thumbnail);

            setHistoryOfUsedImages(prev => {
                const index = prev.findIndex(file => file.name === thumbnail.name);
                if (index === -1) return [thumbnail, ...prev];

                const next = [...prev];
                next[index] = thumbnail;
                return next;
            });

            setAllImages(prev => {
                const index = prev.findIndex(file => file.name === thumbnail.name);
                if (index === -1) return [thumbnail, ...prev];

                const next = [...prev];
                next[index] = thumbnail;
                return next;
            });

            setAutoSaveActive(false);
            return thumbnail;
        } catch (error) {
            console.error("updateImageProject failed:", error);
            return null;
        }
    };
    const createProjectSnapshot = (): ProjectSaveSnapshot => ({
        image: selectedImage ? { ...selectedImage } : null,
        layers: imageLayer.map(layer => ({
            ...layer,
            position: { ...layer.position },
            imageData: layer.imageData ? { ...layer.imageData } : undefined,
            imageEdit: layer.imageEdit ? { ...layer.imageEdit } : undefined,
            textData: layer.textData ? { ...layer.textData } : undefined,
            drawingData: layer.drawingData ? { ...layer.drawingData } : undefined,
            variations: layer.variations ? [...layer.variations] : undefined
        })),
        createdAt
    });

    const markProjectEdited = () => {
        if (loadProjectDataActiveRef.current) return;

        projectDirtyRef.current = true;
        setProjectDirtyRevision(prev => prev + 1);
    };

    const clearAutosaveTimer = () => {
        if (autosaveTimerRef.current != null) {
            window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
    };
    const flushProjectSaveQueue = async () => {
        if (saveProjectRunningRef.current) return;

        saveProjectRunningRef.current = true;

        try {
            while (pendingProjectSaveRef.current) {
                const snapshot = pendingProjectSaveRef.current;
                pendingProjectSaveRef.current = null;

                await updateImageProject(snapshot);
            }
        } finally {
            saveProjectRunningRef.current = false;
            setAutoSaveActive(false);
        }
    };
    const queueProjectSave = (snapshot: ProjectSaveSnapshot | null, baseVariations: ImageProps[]) => {
        if (!snapshot?.image?.file) return;

        pendingProjectSaveRef.current = snapshot;
        //update project in current list
        updateCurrentList({...snapshot,baseVariarions:baseVariations});
        setAutoSaveActive(true);

        void flushProjectSaveQueue();
    };

    const centerCanvas = () => {
        setPanOffset({ x: 0, y: 0 });
    };
    const resetCanvasView = () => {
        const canvas = canvasRef.current;

        if (canvas && canvas.width > 0 && canvas.height > 0) {
            fitCanvasToViewport(canvas.width, canvas.height);
            return;
        }

        setPanOffset({ x: 0, y: 0 });
    };
    const getCanvasScale = () => {
        const canvas = canvasRef.current;

        if (!canvas || !canvasDisplayRect) {
            return { scaleX: zoom, scaleY: zoom };
        }

        return {
            scaleX: canvasDisplayRect.width / canvas.width,
            scaleY: canvasDisplayRect.height / canvas.height
        };
    };
    const fitExpandOverlayIntoViewport = (padding: ExtendPadding) => {
        const viewport = canvasViewportRef.current;
        const canvas = canvasRef.current;

        if (!viewport || !canvas || !openExtandImage.active) return;

        const isLayerExpand = openExtandImage.target === "layer";

        const targetWidth =
            isLayerExpand && selectedLayer
                ? selectedLayer.width
                : canvas.width;

        const targetHeight =
            isLayerExpand && selectedLayer
                ? selectedLayer.height
                : canvas.height;

        const expandedWidth =
            targetWidth +
            padding.left +
            padding.right +
            EXPAND_HANDLE_SAFE_MARGIN * 2 +
            EXPAND_PREVIEW_RESERVED_WIDTH;

        const expandedHeight =
            targetHeight +
            padding.top +
            padding.bottom +
            EXPAND_HANDLE_SAFE_MARGIN * 2 +
            EXPAND_PREVIEW_RESERVED_HEIGHT;

        const availableWidth = Math.max(160, viewport.clientWidth - 24);
        const availableHeight = Math.max(160, viewport.clientHeight - 24);

        const fitZoom = Math.min(
            1,
            availableWidth / Math.max(1, expandedWidth),
            availableHeight / Math.max(1, expandedHeight)
        );

        setZoom(prev => {
            const next = clampZoom(Math.min(prev, fitZoom));

            if (next < prev) {
                expandZoomWasChangedRef.current = true;
            }

            return next;
        });
    };

    const beginMiddleMouseDrag = (e: React.MouseEvent | MouseEvent) => {
        if (e.button !== 1) return;
        if (!selectedImage) return;
        if (cropMode || rotateMode || openExtandImage.active) return;
        if (editingTextLayerId != null) return;

        e.preventDefault();
        e.stopPropagation();

        //avoid selecting hidden layer
        const selectedLayers = imageLayer.filter((l) =>
            selectedLayerIDs.includes(l.layerID) &&
            !l.hidden
        );

        middleMouseDragRef.current = {
            mode: selectedLayers.length > 0 ? "layers" : "canvas",
            startX: e.clientX,
            startY: e.clientY,
            startPanX: panOffset.x,
            startPanY: panOffset.y,
            startLayers: selectedLayers.map(layer => ({
                ...layer,
                position: { ...layer.position }
            }))
        };

        setIsPanning(true);

        window.addEventListener("mousemove", handleMiddleMouseDragMove);
        window.addEventListener("mouseup", endMiddleMouseDrag);
    };
    const handleMiddleMouseDragMove = (ev: MouseEvent) => {
        const drag = middleMouseDragRef.current;
        if (!drag) return;

        ev.preventDefault();

        const dx = ev.clientX - drag.startX;
        const dy = ev.clientY - drag.startY;

        if (drag.mode === "canvas") {
            setPanOffset({
                x: drag.startPanX + dx,
                y: drag.startPanY + dy
            });

            return;
        }

        const { scaleX, scaleY } = getCanvasScale();

        const moveX = dx / Math.max(scaleX, 0.0001);
        const moveY = dy / Math.max(scaleY, 0.0001);

        const selectedIds = drag.startLayers.map(layer => layer.layerID);

        const nextLayers = imageLayer.map(layer => {
            const startLayer = drag.startLayers.find(item => item.layerID === layer.layerID);

            if (!startLayer) return layer;

            return {
                ...layer,
                position: {
                    x: Math.round(startLayer.position.x + moveX),
                    y: Math.round(startLayer.position.y + moveY)
                }
            };
        });

        commitLayerChange(nextLayers, selectedIds);
    };
    const endMiddleMouseDrag = () => {
        middleMouseDragRef.current = null;
        setIsPanning(false);

        window.removeEventListener("mousemove", handleMiddleMouseDragMove);
        window.removeEventListener("mouseup", endMiddleMouseDrag);
    };
    const handleImagesGenerated = (images: ImageProps[]) => {
        setGeneratedImages(images);
        updateCurrentList({
            image: selectedImage,
            layers: imageLayer,
            createdAt: createdAt,
            baseVariarions: images
        });
    };
    const handleRotateMove = (e: MouseEvent) => {
        if (!isRotating || !canvasWrapperRef.current) return;

        const rect = canvasWrapperRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;

        const angle = Math.atan2(e.clientY - cy, e.clientX - cx);
        const deg = Math.round((angle * 180) / Math.PI);

        setRotationDraft(deg);
    };
    const closeExtendImage = () => {
        resetExpandPadding();
        setOpenExtandImage(prev => ({
            ...prev,
            active: false,
            padding: ZERO_EXTEND_PADDING
        }));

        resetExpandPadding();
        setShowExpandVariationModal(false);
        onChangeExpandImageVariationsActive(false);
        resetExpandZoomIfNeeded();
    };

    const openExpandBaseTransparent = () => {
        if (cropMode || rotateMode || openExtandImage.active) return;
        setCanvasDisplayRect(null);
        resetExpandPadding();
        zoomOutForBaseExpand();
        setCropMode(false);
        setRotateMode(false);
        setOverlayTarget("image");
        setSelectedLayer(null);
        setSelectedLayerIDs([]);
        onChangeWorkOnBaseImage(true);

        lastExpandPaddingRef.current = { top: 0, right: 0, bottom: 0, left: 0 };
        setOpenExtandImage({
            active: true,
            mode: "transparent",
            target: "base",
            padding: { top: 0, right: 0, bottom: 0, left: 0 }
        });
    };
    const openExpandLayerTransparent = () => {
        if (!selectedLayer || selectedLayerIDs.length !== 1) return;
        if (cropMode || rotateMode || openExtandImage.active) return;
        resetExpandPadding();
        setCanvasDisplayRect(null);
        zoomOutForBaseExpand();
        setCropMode(false);
        setRotateMode(false);
        setOverlayTarget("layer");
        onChangeWorkOnBaseImage(false);

        lastExpandPaddingRef.current = { top: 0, right: 0, bottom: 0, left: 0 };
        setOpenExtandImage({
            active: true,
            mode: "transparent",
            target: "layer",
            padding: { top: 0, right: 0, bottom: 0, left: 0 }
        });
    };

    const openExpandWithVariations = (target: "base" | "layer") => {
        if (cropMode || rotateMode || openExtandImage.active) return;
        setCanvasDisplayRect(null);
        resetExpandPadding();
        if (target === "layer" && (!selectedLayer || selectedLayerIDs.length !== 1)) return;
        
        zoomOutForBaseExpand();

        setCropMode(false);
        setRotateMode(false);
        setOverlayTarget(target === "base" ? "image" : "layer");
        onChangeWorkOnBaseImage(target === "base");

        setOpenExtandImage({
            active: true,
            mode: "variations",
            target,
            padding: { top: 0, right: 0, bottom: 0, left: 0 }
        });
    };
    const saveBaseTransparentExtend = async (
        padding: ExtendPadding,
        closeAfterSave = true
    ) => {
        if (!selectedImage?.src) return;

        const img = await loadImage(selectedImage.src);

        const nextWidth = img.width + padding.left + padding.right;
        const nextHeight = img.height + padding.top + padding.bottom;

        const canvas = document.createElement("canvas");
        canvas.width = nextWidth;
        canvas.height = nextHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, nextWidth, nextHeight);
        ctx.drawImage(img, padding.left, padding.top);

        const baseName = stripKnownImageStudioExtensions(
            selectedImage.file?.name ||
            selectedImage.title ||
            "image"
        );

        const file = await canvasToFile(canvas, `${baseName}.png`);
        // eslint-disable-next-line no-restricted-syntax -- internes Editier-State-Update (kein Download), Art. 50 KI-VO nicht einschlaegig
        const src = canvas.toDataURL("image/png");

        setImageChangeSource("edit");
        setSelectedImage({
            file,
            src,
            width: nextWidth,
            height: nextHeight,
            timeOfUse: new Date(),
            title: baseName
        });
        markProjectEdited();

        const shiftedLayers = imageLayer.map(layer => ({
            ...layer,
            position: {
                x: layer.position.x + padding.left,
                y: layer.position.y + padding.top
            }
        }));

        commitLayerChange(shiftedLayers, []);
        markProjectEdited();

        if (closeAfterSave) {
            closeExtendImage();
        }
    };

    const saveVariationExtendSelection = async () => {
        const { target } = openExtandImage;
        const padding = expandPadding;

        //expand transparent before generation if image to image, not for outpaint
        // if (target === "base") {
        //     await saveBaseTransparentExtend(padding, false);
        // } else {
        //     await extendSelectedLayerTransparent(padding);
        // }

        onChangeWorkOnBaseImage(target === "base");
        onChangeExpandImageVariationsActive(true);
        setShowExpandVariationModal(true);
        setOpenExtandImage(prev => ({
            ...prev,
            active: false,
            padding
        }));

        resetExpandZoomIfNeeded();
    };
    const saveExtendImage = async () => {
        if (!openExtandImage.active) return;

        if (openExtandImage.mode === "transparent") {
            if (openExtandImage.target === "base") {
                await saveBaseTransparentExtend(openExtandImage.padding);
            } else {
                await extendSelectedLayerTransparent(openExtandImage.padding);
                closeExtendImage();
            }
            return;
        }

        await saveVariationExtendSelection();
    };
    const EXPAND_LAYOUT_MARGIN = 100;
    const EXPAND_VIEWPORT_MIN_SPACE = 800;
    const expandViewportMinSpace =
        openExtandImage.target === "layer"
            ? 400
            : EXPAND_VIEWPORT_MIN_SPACE;

    const isExpandActive = openExtandImage.active;

    const getExpandLayoutPadding = (): ExtendPadding => {
        if (!isExpandActive) return ZERO_EXTEND_PADDING;

        const isLayerExpand = openExtandImage.target === "layer";

        const canvas = canvasRef.current;
        const baseW = canvas?.width ?? selectedImage?.width ?? 1;
        const baseH = canvas?.height ?? selectedImage?.height ?? 1;

        const sizeBasedX = Math.round(baseW * 0.12);
        const sizeBasedY = Math.round(baseH * 0.12);

        const baseExtraX = Math.max(120, Math.min(260, sizeBasedX));
        const baseExtraY = Math.max(120, Math.min(260, sizeBasedY));

        const layerExtra = 64;

        const defaultX = isLayerExpand ? layerExtra : baseExtraX;
        const defaultY = isLayerExpand ? layerExtra : baseExtraY;

        const minMarginX = isLayerExpand ? 72 : EXPAND_LAYOUT_MARGIN;
        const minMarginY = isLayerExpand ? 72 : EXPAND_LAYOUT_MARGIN;

        return {
            top: Math.max(
                openExtandImage.padding.top + defaultY,
                minMarginY,
                EXPAND_PREVIEW_RESERVED_HEIGHT
            ),
            right: Math.max(
                openExtandImage.padding.right + defaultX,
                minMarginX,
                EXPAND_PREVIEW_RESERVED_WIDTH
            ),
            bottom: Math.max(
                openExtandImage.padding.bottom + defaultY,
                minMarginY,
                EXPAND_HANDLE_SAFE_MARGIN
            ),
            left: Math.max(
                openExtandImage.padding.left + defaultX,
                minMarginX,
                EXPAND_HANDLE_SAFE_MARGIN
            )
        };
    };
    const getExpandScrollMinSize = () => {
        if (!isExpandActive) {
            return {
                width: "100%" as const,
                height: "100%" as const
            };
        }

        const canvas = canvasRef.current;

        const baseWidth =
            openExtandImage.target === "layer" && selectedLayer
                ? selectedLayer.width
                : canvas?.width ?? selectedImage?.width ?? 1;

        const baseHeight =
            openExtandImage.target === "layer" && selectedLayer
                ? selectedLayer.height
                : canvas?.height ?? selectedImage?.height ?? 1;

        const layoutPadding = getExpandLayoutPadding();

        const expandedWidth =
            baseWidth +
            openExtandImage.padding.left +
            openExtandImage.padding.right +
            layoutPadding.left +
            layoutPadding.right;

        const expandedHeight =
            baseHeight +
            openExtandImage.padding.top +
            openExtandImage.padding.bottom +
            layoutPadding.top +
            layoutPadding.bottom;

        return {
            width: Math.max(expandViewportMinSpace, Math.ceil(expandedWidth * zoom)),
            height: Math.max(expandViewportMinSpace, Math.ceil(expandedHeight * zoom))
        };
    };
    const lastExpandPaddingRef = useRef<ExtendPadding>(ZERO_EXTEND_PADDING);
    const expandPaddingFrameRef = useRef<number | null>(null);
    const pendingExpandPaddingRef = useRef<ExtendPadding | null>(null);

    const keepAnchorVisible = (anchor?: { clientX: number; clientY: number }) => {
        if (!anchor) return;

        const viewport = canvasViewportRef.current;
        if (!viewport) return;

        const rect = viewport.getBoundingClientRect();
        const margin = 80;

        if (anchor.clientX < rect.left + margin) {
            viewport.scrollLeft -= rect.left + margin - anchor.clientX;
        }

        if (anchor.clientX > rect.right - margin) {
            viewport.scrollLeft += anchor.clientX - (rect.right - margin);
        }

        if (anchor.clientY < rect.top + margin) {
            viewport.scrollTop -= rect.top + margin - anchor.clientY;
        }

        if (anchor.clientY > rect.bottom - margin) {
            viewport.scrollTop += anchor.clientY - (rect.bottom - margin);
        }
    };
    const handleChangeExtendPadding = (
        padding: ExtendPadding,
        anchor?: { clientX: number; clientY: number }
    ) => {
        const normalizedPadding: ExtendPadding = {
            top: Math.max(0, Math.round(padding.top || 0)),
            right: Math.max(0, Math.round(padding.right || 0)),
            bottom: Math.max(0, Math.round(padding.bottom || 0)),
            left: Math.max(0, Math.round(padding.left || 0))
        };

        pendingExpandPaddingRef.current = normalizedPadding;

        if (expandPaddingFrameRef.current != null) return;

        expandPaddingFrameRef.current = requestAnimationFrame(() => {
            const next = pendingExpandPaddingRef.current;
            if (!next) return;

            lastExpandPaddingRef.current = next;
            setExpandPadding(next);

            setOpenExtandImage(prev => ({
                ...prev,
                padding: next
            }));

            fitExpandOverlayIntoViewport(next);

            requestAnimationFrame(() => {
                updateCanvasDisplayRect();
                keepAnchorVisible(anchor);
            });

            expandPaddingFrameRef.current = null;
        });
    };
    const handleDropImageOnSelectedImageLayer = async (file: File) => {
        const img = await fileToImageProps(file);
        setSelectedImageLayerImage(img);
        applySelectedImageLayerSource(img);
    };
    const handleDropImageOnCanvas = async (file: File, x: number, y: number) => {
        if (!selectedImage || selectedImage.src === "") {
            await createNewImageEntry(file);
            return;
        }

        const img = await fileToImageProps(file);

        const canvas = canvasRef.current;
        if (!canvas || !canvasDisplayRect) return;

        const canvasX = ((x - canvasDisplayRect.left) / canvasDisplayRect.width) * canvas.width;
        const canvasY = ((y - canvasDisplayRect.top) / canvasDisplayRect.height) * canvas.height;

        createImageLayerFromImage(img, canvasX, canvasY);
    };
    const getThumbnailFileByName = (name: string): File | null => {
        return (
            currentImages.find(file => file.name === name) ??
            historyOfUsedImages.find(file => file.name === name) ??
            allImages.find(file => file.name === name) ??
            null
        );
    };
    const resolveGalleryDragImageFile = async (
        thumbnailName: string
    ): Promise<File | null> => {
        const thumbnailFile = getThumbnailFileByName(thumbnailName);
        if (!thumbnailFile) return null;

        return await loadProjectPngFromThumbnail(
            thumbnailFile,
            MAIN_DATA_PATH_IMAGES
        );
    };
    const handleDropGalleryImageOnCanvas = async (
        thumbnailName: string,
        x: number,
        y: number
    ) => {
        const thumbnailFile = getThumbnailFileByName(thumbnailName);
        if (!thumbnailFile) return;

        if (!selectedImage || selectedImage.src === "") {
            await handleDropBaseImage(thumbnailFile);
            return;
        }

        const pngFile = await loadProjectPngFromThumbnail(
            thumbnailFile,
            MAIN_DATA_PATH_IMAGES
        );

        await handleDropImageOnCanvas(pngFile, x, y);
    };
    const handleDropGalleryImageOnSelectedImageLayer = async (
        thumbnailName: string
    ) => {
        const thumbnailFile = getThumbnailFileByName(thumbnailName);
        if (!thumbnailFile) return;

        const pngFile = await loadProjectPngFromThumbnail(
            thumbnailFile,
            MAIN_DATA_PATH_IMAGES
        );

        await handleDropImageOnSelectedImageLayer(pngFile);
    };
    const handleGalleryImageDragStart = (
        event: React.DragEvent,
        index: number,
        img: ImageProps
    ) => {
        if (!img.file) return;
        clearHoverPreview();

        event.dataTransfer.setData(
            "application/x-sengi-gallery-thumbnail",
            img.file.name
        );

        event.dataTransfer.setData("text/plain", img.file.name);
        event.dataTransfer.effectAllowed = "copy";
        event.dataTransfer.dropEffect = "copy";
    };
    const updateCurrentList = (project: ProjectSaveSnapshotWithVariations) => {
        if(!project.image || !project.image.file) return;
        const projectName = project.image.file.name;
        const projectNamesList = currentImageObjects.map(item => item.image?.file?.name);
        //update if entry exists
        if(projectName && projectNamesList.includes(projectName)){
            const idx = projectNamesList.indexOf(projectName);
            const updatedCurrentList = [...currentImageObjects];
            updatedCurrentList[idx] = {...project};
            setCurrentImageObjects(updatedCurrentList);
        }
        //add to current list object if not
        else{
            setCurrentImageObjects(prev => [{...project},...prev]);
        }
    };
    const openBaseImageFile = async (file: File) => {
        //open image from gallery
        if(file.name.split('.').length > 1 && file.name.endsWith(".thumbnail")){
            //if entry was deleted and only exists on current 
            if(!allImages.map(file=> file.name).includes(file.name)){
                //file upload as new image project zip
                const combinedLists = [...currentImages,...historyOfUsedImages];
                const fullSizeFile = combinedLists.find(item => item.name === file.name) ?? file;
                const newDate = new Date();
                const img = await fileToImageProps(fullSizeFile);
                await uploadProjectData(img,[],MAIN_DATA_PATH_IMAGES,newDate,ThumbnailSize);
                setAllImages(prev => [file,...prev]);
            }
            let loadedPropject: ProjectSaveSnapshot | null = null;
            //load project data open from current list if project was used in session otherwise load from data storage
            const projectname = stripKnownImageStudioExtensions(file.name);
            const projectNamesList = currentImageObjects.map(item => stripKnownImageStudioExtensions(item.image?.file?.name ?? ''));
            if(projectname && projectNamesList.includes(projectname)){
                const idx =  projectNamesList.indexOf(projectname);
                loadedPropject = currentImageObjects[idx];
                setBaseImageVariarions(currentImageObjects[idx].baseVariarions);
            }
            else{
                loadedPropject = await openImageProject(file.name,MAIN_DATA_PATH_IMAGES);
                setBaseImageVariarions([]);
            }
            if(!loadedPropject) return;
            
            setImageChangeSource("external");
            setSelectedImage(loadedPropject.image);
            setImageLayer(loadedPropject.layers);
            setCreatedAt(loadedPropject.createdAt);
            projectDirtyRef.current = false;
            lastLayerDirtyRef.current = loadedPropject.layers;
            setAutoSaveActive(false);
            //add selected image to current layer if not included
            if(!currentImages.map(file => file.name).includes(file.name)){
                setCurrentImages(prev => [file,...prev]);
            }
            //add selected image if not in cookie history
            if(!historyOfUsedImages.map(file => file.name).includes(file.name)){
                updateCookieHistory(History_Cookie_name,file);
                setHistoryOfUsedImages(prev => [file,...prev]);
            }
        }
        //open by drop (new image -> redirect from create new image entry for data storage)
        else{
            //set image as new base image
            await createNewImageEntry(file, false);
            return;
        }

        setHoverPreview(null);
        setSelectedLayer(null);
        setSelectedLayerIDs([]);
        setLayerAreaSelection(null);
        onChangeExpandImageVariationsActive(false);

        setZoom(1);
        setPanOffset({ x: 0, y: 0 });  
    };
    const handleDropBaseImage = async (file: File) => {
        if (!beginProjectLoad()) return;

        try {
            clearAutosaveTimer();

            const previousSnapshot = createProjectSnapshot();
            projectDirtyRef.current = false;
            queueProjectSave(previousSnapshot,[]);

            await openBaseImageFile(file);
        } catch (error) {
            console.error("handleDropBaseImage failed:", error);
        } finally {
            endProjectLoad();
        }
    };
    const resetTransientTools = () => {
        setSelectedDrawingTool("");
        setIsPickingDrawingColor(false);
        setIsPickingTextColor(false);
        setIsPickingBackgroundColor(false);
        setIsPickingBorderColor(false);
        setIsPickingImageLayerBackgroundColor(false);
        setIsPickingImageLayerBorderColor(false);
        setIsPickingTransparencyColor(false);
        setIsPickingTransparencyTransitionColor(false);
    };
    const setSafeToolkitMode = (mode: BasicToolkitMode | null) => {
        resetTransientTools();
        setActiveToolkitMode(mode);
    };
    const handleSelectBaseImage = () => {
        setSelectedLayer(null);
        setSelectedLayerIDs([]);
        setSelectedVariationIndex(null);
        setLayerAreaSelection(null);
        setIsMarqueeArea(false);
    };
    const handleSelectedVariationChange = (index: number, item: ImageProps | null) => {
        if (!selectedLayer && !item) {
            setSelectedVariationIndex(null);
            setHoverPreview(null);
            return;
        }
        //set variation for layer if selected layer else for base image
        if(!selectedLayer && selectedLayerIDs.length < 1){
            setHoverPreview(item);
        }
        setSelectedVariationIndex(index);
    };
    const handleCancelSelectedVariation = () => {
        clearImagePreviewState();
    };
    const handleReplaceBaseImage = (img: ImageProps) => {
        setImageChangeSource("edit");
        setSelectedImage(img);
        setIsBaseImageVariationSelected(false);

        const baseHistoryEntry = inputEditFromSidebar ?? ({} as SettingsPanelEditInputProps);

        const nextHistoryEntry: SettingsPanelEditInputProps = {
            ...baseHistoryEntry,
            title: selectedImage && selectedImage.file 
                ?   stripKnownImageStudioExtensions(selectedImage.file.name)
                :   selectedImage && selectedImage.title
                    ? stripKnownImageStudioExtensions(selectedImage.title)
                    : img.file?.name
                        ? stripKnownImageStudioExtensions(img.file.name)
                        : 'image',
            imgSrc: img.src,
            imgFile: img.file,
            width: img.width,
            height: img.height,
            rotation: inputEditFromSidebar?.rotation ?? 0
        };

        setInputEditFromSidebar(nextHistoryEntry);

        setHistoryOfEditedImages((prev) => [
            ...prev,
            nextHistoryEntry
        ]);
        markProjectEdited();
    };
    const handleHoverVariationBaseImage = (img:ImageProps | null) => {
        if(!isBaseImageVariationSelected){
            setHoverPreview(img);
        }
    };

    const handleSetVariationOnImage = async (img: ImageProps, targetLayer: LayerProps | null) => {
        if (!selectedImage?.src || !img?.src || !targetLayer) return;

        const base = new Image();
        const overlay = new Image();

        base.crossOrigin = "anonymous";
        overlay.crossOrigin = "anonymous";

        await Promise.all([
            new Promise((resolve, reject) => {
                base.onload = resolve;
                base.onerror = reject;
                base.src = selectedImage.src;
            }),
            new Promise((resolve, reject) => {
                overlay.onload = resolve;
                overlay.onerror = reject;
                overlay.src = img.src;
            })
        ]);

        const canvas = document.createElement("canvas");
        canvas.width = selectedImage.width;
        canvas.height = selectedImage.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.drawImage(base, 0, 0, canvas.width, canvas.height);

        ctx.drawImage(
            overlay,
            targetLayer.position.x,
            targetLayer.position.y,
            targetLayer.width,
            targetLayer.height
        );

        // eslint-disable-next-line no-restricted-syntax -- internes Editier-State-Update (kein Download), Art. 50 KI-VO nicht einschlaegig
        const src = canvas.toDataURL("image/png");
        const file = await fetch(src)
            .then((r) => r.blob())
            .then((blob) => new File([blob], "base-image-with-variation.png", { type: "image/png" }));
        setImageChangeSource("edit");
        setSelectedImage({
            ...selectedImage,
            src,
            file,
            width: canvas.width,
            height: canvas.height,
            timeOfUse: new Date()
        });
    };
    const clearLayerSidebarItems = async() => {
        const currentImage = selectedImage ? {...selectedImage} : null;
        const currentImageLayer = {...imageLayer};

        updateCurrentList({
            image: currentImage,
            layers: currentImageLayer,
            createdAt: createdAt,
            baseVariarions: baseImageVariations
        });
        
        setSelectedLayer(null);
        setSelectedLayerIDs([]);
        setSelectedVariationIndex(null);
        setLayerAreaSelection(null);
        setIsMarqueeArea(false);
        setImageLayer([]);
        setSelectedImage(null);
        onChangeExpandImageVariationsActive(false);
        resetCanvasView();

        await updateImageProject({
            image: currentImage,
            layers: currentImageLayer,
            createdAt
        });

        setCreatedAt(null);
    };

    const hoverPreviewBaseImage = (target: 'gallery' | 'generation' | "variation",index: number, img: ImageProps | null) => {
        setHoverPreviewSource(img ? target : null);
        setHoverPreview(img);
    };
    const clearHoverPreview = () => {
        setHoverPreviewSource(null);
        setHoverPreview(null);
    };

    const handleWriteLayerOnBaseImage = async (type: 'all' | 'selected',layerIDs?: number[]) => {
        if(!selectedImage) return;
        const targetLayer: LayerProps[] = type === 'all' && imageLayer
            ? [...imageLayer] 
            : type === 'selected' && layerIDs
            ?   [...imageLayer.filter(item => layerIDs.includes(item.layerID) && !item.hidden)]
                : type === 'selected' && selectedLayerIDs
                    ? [...imageLayer.filter(item => selectedLayerIDs.includes(item.layerID) && !item.hidden)]
                    : []
        const newImageCanvas: HTMLCanvasElement = await composeImageWithLayers(selectedImage,targetLayer);
        const baseName = stripKnownImageStudioExtensions(
            selectedImage.file?.name ||
            selectedImage.title ||
            inputEditFromSidebar?.title ||
            "image"
        );

        const file = await canvasToFile(newImageCanvas, `${baseName}.png`);
        const newImg: ImageProps =  await fileToImageProps(file);
        newImg.title = baseName;

        const newLayer = [...imageLayer.filter(item => !targetLayer.map(x => x.layerID).includes(item.layerID))];
        commitLayerChange([...newLayer],[]);
        setImageChangeSource("edit");
        handleReplaceBaseImage(newImg);
    };
    const handleZoomIn = () => {
        setZoom((prev) => clampZoom(prev + 0.1));
    };

    const handleZoomOut = () => {
        setZoom((prev) => clampZoom(prev - 0.1));
    };
    const handleUndo = () => {
        if (!canUndo) return;
        const nextIndex = historyIndex - 1;
        const entry = historyOfEditedImages[nextIndex];
        if (!entry) return;

        setHistoryIndex(nextIndex);
        applyEditHistoryEntry(entry);
    };

    const handleRedo = () => {
        if (!canRedo) return;
        const nextIndex = historyIndex + 1;
        const entry = historyOfEditedImages[nextIndex];
        if (!entry) return;

        setHistoryIndex(nextIndex);
        applyEditHistoryEntry(entry);
    };

    const handleReset = () => {
        if (!historyOfEditedImages.length) return;

        const firstEntry = historyOfEditedImages[0];
        if (!firstEntry) return;

        setHistoryIndex(0);
        applyEditHistoryEntry(firstEntry);
        setZoom(1);
        setPanOffset({ x: 0, y: 0 });
    };
    const settingsPanelActionRef = useRef({
        flipCanvasHorizontal: async () => {},
        flipCanvasVertical: async () => {},
        openCropCanvas: () => {}
    });

    const flipCanvasHorizontalFromSidebar = () => {
        void settingsPanelActionRef.current.flipCanvasHorizontal();
    };

    const flipCanvasVerticalFromSidebar = () => {
        void settingsPanelActionRef.current.flipCanvasVertical();
    };

    const openCropCanvasFromSidebar = () => {
        settingsPanelActionRef.current.openCropCanvas();
    };
    const openRotate = (deg: number,layerId?: number,target: "base" | "layer" = layerId != null ? "layer" : "base") => {
        const currentRotation =
            target === "layer" && layerId != null
                ? imageLayer.find((layer) => layer.layerID === layerId)?.rotation ?? 0
                : inputEditFromSidebar?.rotation ?? 0;

        const nextRotation = currentRotation + deg;

        setRotationDraft(nextRotation);
        setCropMode(false);
        setRotateMode(false);

        void handleRotateSave(nextRotation, target === "layer" ? layerId : undefined, target);
    };
    const openExpandTransparentForCurrentTarget = () => {
        if (!selectedImage) return;
        if (cropMode || rotateMode || openExtandImage.active) return;

        if (selectedLayerIDs.length === 1 && selectedLayer) {
            if (selectedLayer.hidden) return;
            if (selectedLayer.type === "text") return;

            openExpandLayerTransparent();
            return;
        }

        openExpandBaseTransparent();
    };
    const handleDeleteVariations = (index:number, targetLayer?:LayerProps | null) => {
        //delete selected variation in base
        if(!targetLayer && index < baseImageVariations.length){
            const newVariations = [...baseImageVariations]
            newVariations.splice(index,1);
            setBaseImageVariarions(newVariations);
        }
        else if(targetLayer){
            const newLayer = imageLayer.map(item => {
                if(item.layerID !== targetLayer.layerID){
                    return item;
                }
                else {
                    let newLayer = item;
                    if(item.variations){
                        item.variations.splice(index,1);
                    }
                    return newLayer;
                }
            });

            setImageLayer(newLayer);
        }
    };
    

    //get layer data
    const [
        imageLayer,
        setImageLayer,
        overlayLayerRect,
        layerAreaSelection,
        marqueeSelectionPreview,
        selectionPreview,
        activeOverlayBaseLayerRect,
        activeLayerEditId,
        handleCropSelectedLayerSave,
        rotateSelectedLayer,
        updatePointerSelection,
        beginLayerTransform, 
        beginSelectionDrag,
        beginDrawingStroke, 
        beginPointerMarqueeSelection,
        setLayerAreaSelection,
        setOverlayLayerRect,
        deleteSelectedAreaFromLayer,
        copySelectedAreaFromLayer,
        buildAiReferenceLayerFromSelection,
        buildAiReferenceLayerFromLayer,
        extractSelectedAreaToNewLayer,
        duplicateSelectedLayer,
        drawingCursorPreview,
        drawingStrokePreview,
        shapePreview,
        toggleSelectedTextStyle,
        applySelectedTextContent,
        setSelectedTextColor,
        setSelectedTextOpacity,
        applySelectedTextSize,
        setSelectedTextFontFamily,
        setSelectedTextBorderColor,
        setSelectedTextBorderOpacity,
        setSelectedTextBorderWidth,
        setSelectedTextBorderStyle,
        setSelectedTextBackgroundColor,
        setSelectedTextBackgroundOpacity,
        applySelectedImageLayerSource,
        applySelectedImageLayerOpacity,
        applySelectedImageLayerBackgroundColor,
        applySelectedImageLayerBackgroundOpacity,
        applySelectedImageLayerBorderColor,
        applySelectedImageLayerBorderOpacity,
        applySelectedImageLayerBorderWidth,
        setSelectedImageLayerBorderStyle,
        createImageLayerFromImage,
        undoSelectedLayerContentChange,
        redoSelectedLayerContentChange,
        commitLayerChange,
        extendSelectedLayerTransparent,
        onChangeLayerToBack,
        onChangeLayerToFront,
        layerSideBar
    ] = useLayerFunctionAndSidebar({
        image: selectedImage,
        imageChangeSource: imageChangeSource,
        canvasRef: canvasRef,
        canvasWrapperRef: canvasWrapperRef,
        canvasDisplayRect: canvasDisplayRect,
        cropMode: cropMode,
        rotateMode: rotateMode,
        isClosingOverlays: isClosingOverlays,
        isRotating: isRotating,
        rotationDraft: rotationDraft,
        selectedLayer: selectedLayer,
        selectedLayerIDs: selectedLayerIDs,
        activeToolkitTool:activeToolkitMode,
        toolkitAction:toolkitAction,
        baseImage: selectedImage,
        selectedVariationIndex: selectedVariationIndex,
        handleWriteLayerOnBaseImage: handleWriteLayerOnBaseImage,
        onSetVariationOnImage: handleSetVariationOnImage,
        onSelectedVariationChange: handleSelectedVariationChange,
        onCancelSelection: handleCancelSelectedVariation,
        onSelectBaseImage: handleSelectBaseImage,
        onSelectedLayerChange:setSelectedLayer,
        onSelectedLayerIDsChange:setSelectedLayerIDs,
        onToggleCropeMode: setCropMode,
        onChangeRotationDraft: setRotationDraft,
        onIsRotating: setIsRotating,
        onRotatePreviewChange: setIsRotatePreviewActive,
        onToggleRotationMode: setRotateMode,
        setIsClosingOverlays: setIsClosingOverlays,
        overlayTarget: overlayTarget,
        baseImageVariations: baseImageVariations,
        isBaseImageVariationSelected: isBaseImageVariationSelected,
        handleRotateMove: handleRotateMove,
        onBaseImageReplace: handleReplaceBaseImage,
        onVariationHoverBaseImage: handleHoverVariationBaseImage,
        selectedDrawingColor: pickedDrawingColor,
        selectedBrushSize: selectedBrushSize,
        selectedBrushHardness: selectedBrushHardness,
        selectedBrushOpacity: selectedBrushOpacity,
        selectedDrawingTool: selectedDrawingTool,
        resetCanvasView: resetCanvasView,
        clearSidebarItems: clearLayerSidebarItems,
        handleZoomIn: handleZoomIn,
        handleZoomOut: handleZoomOut,
        handleRedoBase: handleRedo,
        handleResetBase: handleReset,
        handleUndoBase: handleUndo,
        flipCanvasHorizontal: flipCanvasHorizontalFromSidebar,
        flipCanvasVertical: flipCanvasVerticalFromSidebar,
        onCropBaseImage: openCropCanvasFromSidebar,
        openRotate: openRotate,
        onDeleteVariation: handleDeleteVariations,
        openExtandImage: openExtandImage,
    });
    const selectedLayerForHistory =
        selectedLayerIDs.length === 1
            ? imageLayer.find((layer) => layer.layerID === selectedLayerIDs[0]) ?? null
            : null;

    const canUndoSelectedLayer =
        !!selectedLayerForHistory?.history?.past?.length;

    const canRedoSelectedLayer =
        !!selectedLayerForHistory?.history?.future?.length;
    
    const handleNewVariations = (layer: LayerProps | null, toBaseImage: boolean, variarions: ImageProps []) => {
        // if no layer was selected and no area marqued, variations will be set to base image
        if(toBaseImage){
            setBaseImageVariarions(variarions);
        }
        else{
            if(!layer) return;
            //if layer is selected and no are marked: variation will be set to layer 
            if(imageLayer.map(item => item.layerID).includes(layer.layerID)){
                const newLayer = imageLayer.map((item) => {
                    if (layer.layerID !== item.layerID){
                        return item;
                    }

                    return {
                        ...item,
                        variations: variarions
                    };
                });
                setImageLayer(newLayer);
            }
            //if marqued area a new layer will be created undependent if layer was selected or for base image: position of marquee needed
            else{
                const newLayer: LayerProps = {
                    type :  'ai-variation',
                    layerID: layer.layerID,
                    position: layer.position,
                    width: layer.width,
                    height: layer.height,
                    orderNumber: layer.orderNumber,
                    hidden: false,            
                    imageData: variarions.length > 0 ? variarions[0] : undefined,
                    imageEdit: variarions.length > 0 ? variarions[0] : undefined,
                    cropRect: null,
                    rotation: 0,
                    flipHorizontal: false,
                    flipVertical: false,
                    variations: variarions
                }
                const newLayers = [...imageLayer, newLayer];
                setImageLayer(newLayers);
            }
        }
    };
    const handleSelectImage = async (index: number, item: ImageProps) => {
        if (!item.file) return;
        if (!beginProjectLoad()) return;

        try {
            clearAutosaveTimer();
            const previousSnapshot = createProjectSnapshot();
            projectDirtyRef.current = false;
            queueProjectSave(previousSnapshot,baseImageVariations);
            clearImagePreviewState();
            await openBaseImageFile(item.file);
        } catch (error) {
            console.error("handleSelectImage failed:", error);
        } finally {
            endProjectLoad();
        }
    };
    // get settingssidebar data
    const [
        overlayImageRect, 
        setOverlayImageRect, 
        activeOverlayBaseImageRect, 
        rectToVisibleCanvasCrop, 
        runGeometryEdit,
        settingsSideBar,
        workOnBaseImage, 
        onChangeWorkOnBaseImage,
        expandImageVariationsActive,
        onChangeExpandImageVariationsActive,
        flipCanvasHorizontal,
        flipCanvasVertical,
        openCropCanvas,
        aiGenerationsState,
        imageEditCosts,
        handleGenerateVariations
    ] = SettingsPanel({
        switchImageCreateEdit:true,
        aiModelList: aiModelList,
        generateRandomPrompt:true,
        checkPrompt:true,
        samplingSteps:true,
        samplerList:samplerList,//,'Euler a','DPM++ 2M','DPM++ 2M Karras','DPM++ SDE','DDIM','PLMS','UniPC']}
        schedulerList: schedulerList,
        seed:true,
        stylesList:stylesList,
        aspectRatioList: aspectRatioList,
        creativityRange:true,
        qualityList: qualityList,
        numberGenerationsMax:10,
        numberOfGenerationsText:t('page.numberOfImages'),
        createImageButtonText:t('page.createButton'),
        editImageButtonText:t('page.editButton'),
        checkPromptText:t('page.checkPromptButton'),
        generateRandomPromptText:t('page.randomPromptButton'),
        addReferences:true,
        addReferenceText:t('page.addImage'),
        historyOfEditedImages:historyOfEditedImages,
        onGenerations:handleImagesGenerated,
        onItemSelect:handleSelectImage,
        buildAiReferenceLayerFromSelection: buildAiReferenceLayerFromSelection,
        buildAiReferenceLayerFromLayer: buildAiReferenceLayerFromLayer,
        image:selectedImage,
        canvasRef:canvasRef,
        canvasViewportRef:canvasViewportRef,
        canvasDisplayRect:canvasDisplayRect,
        isPickingTransparencyColor:isPickingTransparencyColor,
        isPickingTransparencyTransitionColor:isPickingTransparencyTransitionColor,
        onToggleTransparencyColorPicker: () => void startGlobalColorPicker("transparencyColor"),
        onToggleTransparencyTransitionColorPicker: () => void startGlobalColorPicker("transparencyTransitionColor"),
        pickedTransparencyColor: pickedTransparencyColor,
        pickedTransparencyTransitionColor: pickedTransparencyTransistionColor,
        onTransparencyColorPicked: (color:string) => setPickedTransparencyColor(color),
        onTransparencyTransitionColorPicked:(color:string) => setPickedTransparencyTransitionColor(color),
        canvasWrapperRef:canvasWrapperRef,
        onItemHover:setHoverPreview,
        onItemHoverEnd:() => setHoverPreview(null),
        onCreateNewBaseImageEntry: createNewImageEntry,
        onReplaceBaseImageDirectly:handleReplaceBaseImage,
        centerCanvas: centerCanvas,
        onImageEdited: (editedImage: SettingsPanelEditInputProps) => {
            if(editedImage.imgFile){
                setImageChangeSource("edit");
                const baseName = stripKnownImageStudioExtensions(
                    selectedImage?.file?.name ||
                    selectedImage?.title ||
                    editedImage.title ||
                    editedImage.imgFile?.name ||
                    "image"
                );

                let newSelectedImage: ImageProps = {
                    src: editedImage.imgSrc,
                    file: new File(
                        [editedImage.imgFile],
                        `${baseName}.png`,
                        {
                            type: editedImage.imgFile.type || "image/png",
                            lastModified: editedImage.imgFile.lastModified
                        }
                    ),
                    width: editedImage.width,
                    height: editedImage.height,
                    timeOfUse: new Date(),
                    title: baseName
                };
                setSelectedImage(newSelectedImage);
                markProjectEdited();
            }
            setHistoryOfEditedImages(prev => [
            ...prev,
            editedImage
            ]);
        },
        openExtandImage: openExtandImage,
        onOpenExpandBaseTransparent: openExpandBaseTransparent,
        onOpenExpandBaseWithVariations: () => openExpandWithVariations("base"),
        onOpenExpandLayerWithVariations: () => openExpandWithVariations("layer"),
        onSaveExtendImage: saveExtendImage,
        onCancelExtendImage: closeExtendImage,
        onAiEditInputChange: setInputAiEditFromSidebar,
        onRotatePreviewChange:setIsRotatePreviewActive,
        onChangeRotationDraft:setRotationDraft,
        onToggleCropeMode: setCropMode,
        onToggleRotationMode: setRotateMode,
        cropMode:cropMode,
        rotateMode:rotateMode,
        isRotating:isRotating,
        onIsRotating:setIsRotating,
        handleRotateMove:handleRotateMove,
        onChangeLayer: setImageLayer,
        onHandleVariationResult: handleNewVariations,
        marqueeArea: isMarqueeArea,
        selectedLayer: selectedLayer,
        aiEditReference: aiEditReference,
        allLayer: imageLayer,
        selectedLayerIDs: selectedLayerIDs,
        layerAreaSelection: layerAreaSelection,
        commitLayerChange: commitLayerChange,
        onChangeBaseImageVariations: setBaseImageVariarions,
        onDeleteSelectedAreaFromLayer: deleteSelectedAreaFromLayer,
        onDuplicateSelectedAreaFromLayer: copySelectedAreaFromLayer,
        onExtractSelectedAreaToNewLayer: extractSelectedAreaToNewLayer,
        expandPadding: expandPadding,
        onResetExpandState: () => {
            resetExpandPadding();
            onChangeExpandImageVariationsActive(false);
        }
    });
    settingsPanelActionRef.current = {
        flipCanvasHorizontal,
        flipCanvasVertical,
        openCropCanvas
    };

    const startTextEdit = (layer: LayerProps) => {
        if (layer.type !== "text" || !layer.textData) return;

        if (
            activeToolkitMode === "hand" ||
            activeToolkitMode === "select-rect" ||
            activeToolkitMode === "select-oval" ||
            activeToolkitMode === "select-free" ||
            isAnyColorPickingActive
        ) {
            resetTransientTools();
            setActiveToolkitMode("pointer");
        }

        setSelectedLayer(layer);
        setSelectedLayerIDs([layer.layerID]);
        setEditingTextLayerId(layer.layerID);
        setEditingTextValue(layer.textData.text || "");
    };

    const changeEditingText = (text: string) => {
        editingTextValueRef.current = text;
        setEditingTextValue(text);
        applySelectedTextContent(editingTextValueRef.current);
    };

    const commitTextEdit = () => {
        if (editingTextLayerId == null) return;
        applySelectedTextContent(editingTextValueRef.current);
        setEditingTextLayerId(null);
    };
    const clearImagePreviewState = () => {
        setHoverPreview(null);
        setSelectedVariationIndex(null);
        setIsBaseImageVariationSelected(false);
    };

    const activateCanvasColorPicker = (target: ColorPickTarget) => {
        stopPickingColor();
        setActiveToolkitMode(null);
        setSelectedDrawingTool("");

        if (target === "drawingColor") {
            setIsPickingDrawingColor(true);
        } else if (target === "transparencyColor") {
            setIsPickingTransparencyColor(true);
        } else if (target === "transparencyTransitionColor") {
            setIsPickingTransparencyTransitionColor(true);
        } else if (target === "textColor") {
            setIsPickingTextColor(true);
        } else if (target === "backgroundColorText") {
            setIsPickingBackgroundColor(true);
        } else if (target === "borderColorText") {
            setIsPickingBorderColor(true);
        } else if (target === "backgroundColorImage") {
            setIsPickingImageLayerBackgroundColor(true);
        } else if (target === "borderColorImage") {
            setIsPickingImageLayerBorderColor(true);
        }
    };
    const handleSetSelectedColor = async (e: React.MouseEvent<HTMLElement>) => {
        if (isPickingDrawingColor) {
            await pickColorFromSurface(e, "drawingColor");
        }
        if (isPickingTransparencyColor) {
            await pickColorFromSurface(e, "transparencyColor");
        }
        if (isPickingTransparencyTransitionColor) {
            await pickColorFromSurface(e, "transparencyTransitionColor");
        }
        if (isPickingTextColor) {
            await pickColorFromSurface(e, "textColor");
        }
        if (isPickingBackgroundColor) {
            await pickColorFromSurface(e, "backgroundColorText");
        }
        if (isPickingBorderColor) {
            await pickColorFromSurface(e, "borderColorText");
        }
        if (isPickingImageLayerBackgroundColor) {
            await pickColorFromSurface(e, "backgroundColorImage");
        }
        if (isPickingImageLayerBorderColor) {
            await pickColorFromSurface(e, "borderColorImage");
        }
    };
    const applyPickedColor = (hex: string, target: ColorPickTarget) => {
        if (target === "drawingColor") {
            setPickedDrawingColor(hex);
        } else if (target === "transparencyColor") {
            setPickedTransparencyColor(hex);
        } else if (target === "transparencyTransitionColor") {
            setPickedTransparencyTransitionColor(hex);
        } else if (target === "textColor") {
            setPickedTextColor(hex);
            setSelectedTextColor(hex);
        } else if (target === "borderColorText") {
            setPickedBorderColor(hex);
            setSelectedTextBorderColor(hex);
        } else if (target === "backgroundColorText") {
            setPickedBackgroundColor(hex);
            setSelectedTextBackgroundColor(hex);
        } else if (target === "backgroundColorImage") {
            setSelectedImageLayerBackgroundColor(hex);
            applySelectedImageLayerBackgroundColor(hex);
        } else if (target === "borderColorImage") {
            setSelectedImageLayerBorderColor(hex);
            applySelectedImageLayerBorderColor(hex);
        }

        stopPickingColor();
    };
    const startGlobalColorPicker = async (target: ColorPickTarget) => {
        stopPickingColor();
        setActiveToolkitMode(null);
        setSelectedDrawingTool("");

        const EyeDropperCtor = window.EyeDropper;

        if (!EyeDropperCtor || !window.isSecureContext) {
            activateCanvasColorPicker(target);
            return;
        }

        try {
            const result = await new EyeDropperCtor().open();
            applyPickedColor(result.sRGBHex, target);
        } catch (error) {
            stopPickingColor();
            console.warn("Global color picking cancelled or failed:", error);
        }
    };
    
    const pickColorFromSurface = async (
        e: React.MouseEvent<HTMLElement>,
        target: ColorPickTarget
    ) => {
        if (!selectedImage || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        if (rect.width === 0 || rect.height === 0) return;

        const x = Math.floor(((e.clientX - rect.left) / rect.width) * canvas.width);
        const y = Math.floor(((e.clientY - rect.top) / rect.height) * canvas.height);

        if (x < 0 || y < 0 || x >= canvas.width || y >= canvas.height) return;

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;

        const ctx = tempCanvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;

        try {
            const baseImg = await loadImage(selectedImage.src);
            ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
            ctx.drawImage(baseImg, 0, 0, tempCanvas.width, tempCanvas.height);

            const visibleLayers = sortLayersByOrderAscending(imageLayer).filter(layer => !layer.hidden);

            for (const layer of visibleLayers) {
                await renderLayerToCanvas(ctx, layer, 0, 0);
            }

            const pixel = ctx.getImageData(x, y, 1, 1).data;

            if (pixel[3] === 0) return;

            const hex = rgbToHex(pixel[0], pixel[1], pixel[2]);
            applyPickedColor(hex, target);
        } catch (error) {
            console.error("Surface color picking failed:", error);
        }
    };
    const dispatchToolkitAction = (type: string) => {
        setToolkitAction({ type, ts: Date.now() });
    };
    const convertToAiReferenceData = async (
        createNewLayer: boolean,
        content: LayerProps | ImageProps
    ) => {
        const selectionContext = layerAreaSelection
            ? await getCurrentSelectionContext()
            : null;

        const aiRefData = {
            type: selectionContext?.sourceType ?? (workOnBaseImage ? "base" : "layer"),
            createNewLayer,
            isMarking: !!layerAreaSelection,
            content,
            selectionContext
        } as AiLayerVariationGenerationProps & {
            selectionContext?: AiSelectionContext | null;
            resultGeometry?: AiSelectionContext["sourceLayerGeometry"];
            resultGeometryMode?: "selection" | "source";
        };

        setAiEditReference(aiRefData);
    };
    const getCurrentSelectionContext = async (): Promise<AiSelectionContext | null> => {
        if (!layerAreaSelection || !selectedImage) return null;

        const selectionLayer =
            layerAreaSelection.layerId !== -1
                ? imageLayer.find(layer => layer.layerID === layerAreaSelection.layerId) ?? selectedLayer
                : null;

        if (!selectionLayer) {
            return {
                sourceType: "base",
                selection: layerAreaSelection,
                sourceImage: selectedImage,
                sourceLayerGeometry: {
                    position: { x: 0, y: 0 },
                    width: selectedImage.width,
                    height: selectedImage.height,
                    rotation: 0
                }
            };
        }

        const layerAsImage = await buildAiReferenceLayerFromLayer(selectionLayer);
        const sourceImage =
            layerAsImage?.imageEdit ??
            layerAsImage?.imageData ??
            selectionLayer.imageEdit ??
            selectionLayer.imageData ??
            null;

        if (!sourceImage) return null;

        return {
            sourceType: "layer",
            selection: layerAreaSelection,
            sourceImage,
            sourceLayerGeometry: {
                position: { ...selectionLayer.position },
                width: selectionLayer.width,
                height: selectionLayer.height,
                rotation: selectionLayer.rotation ?? 0
            }
        };
    };

    //Short cuts
    const isTypingTarget = () => {
        const activeEl = document.activeElement as HTMLElement | null;
        const tag = activeEl?.tagName?.toLowerCase();

        return (
            tag === "input" ||
            tag === "textarea" ||
            tag === "select" ||
            activeEl?.isContentEditable
        );
    };
    const hasSingleEditableLayerSelected = () => {
        return selectedLayerIDs.length === 1 && !!selectedLayer && selectedLayer.type !== "text";
    };
    const openRotateModeForCurrentTarget = () => {
        if (!selectedImage) return;

        setCropMode(false);

        if (hasSingleEditableLayerSelected()) {
            setOverlayTarget("layer");
            dispatchToolkitAction("rotate-selected-layer");
            return;
        }

        setSelectedLayer(null);
        setSelectedLayerIDs([]);
        setOverlayTarget("image");
        setRotationDraft(inputEditFromSidebar?.rotation ?? 0);
        setRotateMode(true);
    };
    const openCropModeForCurrentTarget = () => {
        if (!selectedImage) return;

        setRotateMode(false);

        if (hasSingleEditableLayerSelected()) {
            setOverlayTarget("layer");
            dispatchToolkitAction("crop-selected-layer");
            return;
        }

        setSelectedLayer(null);
        setSelectedLayerIDs([]);
        setOverlayTarget("image");
        openCropCanvas();
    };
    const flipCurrentTarget = (direction: "horizontal" | "vertical") => {
        if (!selectedImage) return;

        if (hasSingleEditableLayerSelected()) {
            dispatchToolkitAction(
                direction === "horizontal"
                    ? "flip-horizontal-layer"
                    : "flip-vertical-layer"
            );
            return;
        }

        if (direction === "horizontal") {
            void flipCanvasHorizontal();
        } else {
            void flipCanvasVertical();
        }
    };
    const normalizeRotationExact = (deg: number) => {
        return ((deg % 360) + 360) % 360;
    };
    const rotateCurrentTargetBy = (delta: number) => {
        if (!selectedImage) return;

        setRotationDraft(prev => normalizeRotationExact(prev + delta));
    };
    const handleHideAllLayers = () => {
        setImageLayer(prev =>
            prev.map(layer => ({
                ...layer,
                hidden: true
            }))
        );

        setSelectedLayer(null);
        setSelectedLayerIDs([]);
    };
    const handleUnhideAllLayers = () => {
        setImageLayer(prev =>
            prev.map(layer => ({
                ...layer,
                hidden: false
            }))
        );
    };

    // UNDO AND REDO
    const canUndo = historyIndex > 0;
    const canRedo = historyIndex >= 0 && historyIndex < historyOfEditedImages.length - 1;

    const applyEditHistoryEntry = (entry: SettingsPanelEditInputProps) => {
        if (!entry.imgFile) return;

        setImageChangeSource('edit');
        const updatedImage: ImageProps = {
            src: entry.imgSrc,
            file: entry.imgFile,
            width: entry.width,
            height: entry.height,
            timeOfUse: new Date(),
            title: entry.title
        };

        setSelectedImage(updatedImage);
        setInputEditFromSidebar(entry);
    };

    // ZOOMING
    const clampZoom = (value: number) => {
        return Math.min(6, Math.max(0.2, value)); 
    };
    const zoomOutForBaseExpand = () => {
        expandZoomWasChangedRef.current = true;

        setZoom(prev => clampZoom(prev * 0.75));
        setPanOffset({ x: 0, y: 0 });

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                updateCanvasDisplayRect();
            });
        });
    };
    const resetExpandZoomIfNeeded = () => {
        if (!expandZoomWasChangedRef.current) return;

        expandZoomWasChangedRef.current = false;
        resetCanvasView();
    };
    const resetExpandPadding = () => {
        lastExpandPaddingRef.current = ZERO_EXTEND_PADDING;
        setExpandPadding(ZERO_EXTEND_PADDING);
    };
    const mergeSelectedLayersByShortcut = () => {
        if (selectedLayerIDs.length < 2) return;

        dispatchToolkitAction("merge-all-selected-layers");
    };
    const writeSelectedLayersToBaseByShortcut = () => {
        if (!selectedImage || selectedLayerIDs.length < 1) return;

        void handleWriteLayerOnBaseImage("selected");
    };
    const hideSelectedLayerByShortcut = () => {
        if (selectedLayerIDs.length < 1) return;

        handleHideSelectedLayer();
    };
    const stripLayerHistoryForShortcut = (layer: LayerProps): LayerProps => {
        const { history, ...rest } = layer;
        return rest as LayerProps;
    };
    const undoCurrentTarget = () => {
        if (selectedLayerIDs.length === 1 && canUndoSelectedLayer) {
            undoSelectedLayerContentChange();
            return;
        }

        handleUndo();
    };
    const redoCurrentTarget = () => {
        if (selectedLayerIDs.length === 1 && canRedoSelectedLayer) {
            redoSelectedLayerContentChange();
            return;
        }

        handleRedo();
    };
    const resetCurrentTarget = () => {
        if (selectedLayerIDs.length === 1) {
            const selectedId = selectedLayerIDs[0];
            const currentLayer = imageLayer.find(layer => layer.layerID === selectedId);
            const firstSnapshot = currentLayer?.history?.past?.[0];

            if (currentLayer && firstSnapshot) {
                const currentSnapshot = stripLayerHistoryForShortcut(currentLayer);

                const restoredLayer: LayerProps = {
                    ...firstSnapshot,
                    history: {
                        past: [],
                        future: [
                            currentSnapshot,
                            ...(currentLayer.history?.future ?? [])
                        ].slice(0, 20)
                    }
                };

                commitLayerChange(
                    imageLayer.map(layer =>
                        layer.layerID === selectedId ? restoredLayer : layer
                    ),
                    [selectedId]
                );

                return;
            }
        }

        handleReset();
    };
    const selectAllLayersByShortcut = () => {
        const visibleLayerIds = imageLayer
            .filter(layer => !layer.hidden)
            .map(layer => layer.layerID);

        if (visibleLayerIds.length < 1) return;

        setSelectedLayer(null);
        setSelectedLayerIDs(visibleLayerIds);
        setLayerAreaSelection(null);
        setIsMarqueeArea(false);
    };
    const deselectAllByShortcut = () => {
        setSelectedLayer(null);
        setSelectedLayerIDs([]);
        setLayerAreaSelection(null);
        setIsMarqueeArea(false);
        setSelectedVariationIndex(null);
    };
    const exportPngByShortcut = () => {
        if (!selectedImage) return;

        handleExportImage("png" as ExportFormat);
    };

    const isAnyColorPickingActive =
        isPickingTransparencyColor ||
        isPickingTransparencyTransitionColor ||
        isPickingDrawingColor ||
        isPickingTextColor ||
        isPickingBackgroundColor ||
        isPickingBorderColor ||
        isPickingImageLayerBackgroundColor ||
        isPickingImageLayerBorderColor;

    const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (isAnyColorPickingActive) return;
        if (activeToolkitMode !== "hand") return;

        setIsPanning(true);
        panStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            startPanX: panOffset.x,
            startPanY: panOffset.y
        };
    };
    const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isPanning || activeToolkitMode !== "hand" || !panStartRef.current) return;

        const dx = e.clientX - panStartRef.current.x;
        const dy = e.clientY - panStartRef.current.y;

        setPanOffset({
            x: panStartRef.current.startPanX + dx,
            y: panStartRef.current.startPanY + dy
        });
    };
    const handleCanvasMouseUp = () => {
        setIsPanning(false);
        panStartRef.current = null;
    };
    const closeOverlays = () => {
        setCropMode(false);
        setRotateMode(false);
        setIsRotating(false);
        setIsClosingOverlays(true);
        setShowExpandVariationModal(false);
        resetExpandPadding();
        setOpenExtandImage(prev => ({
            ...prev,
            active: false,
            padding: { top: 0, right: 0, bottom: 0, left: 0 }
        }));

        lastExpandPaddingRef.current = { top: 0, right: 0, bottom: 0, left: 0 };
    };
    const handleCropSave = async () => {
        try {
            if (overlayTarget === "layer") {
                await handleCropSelectedLayerSave();
            } else {
                const crop = rectToVisibleCanvasCrop(overlayImageRect);
                if (!crop) return;

                await runGeometryEdit(
                    (workCanvas) => cropCanvas(workCanvas, crop),
                    {
                        cropRect: crop,
                        title:
                            selectedImage?.title ||
                            selectedImage?.file?.name ||
                            inputEditFromSidebar?.title ||
                            "image"
                    }
                );
            }
        } catch (error) {
            console.error("Crop failed:", error);
        } finally {
            closeOverlays();
        }
    };
    const handleRotateSave = async (rotationOverride?: number,layerID?: number,target?: "base" | "layer") => {
        const normalizedRotation = normalizeRotationExact(rotationOverride ?? rotationDraft);
        setRotationDraft(normalizedRotation);

        const shouldRotateLayer =
            target === "layer" || (!target && (overlayTarget === "layer" || layerID != null));

        if (shouldRotateLayer) {
            await rotateSelectedLayer(normalizedRotation, layerID);
        } else {
            await runGeometryEdit(
                (workCanvas) => rotateCanvas(workCanvas, normalizedRotation),
                { rotation: normalizedRotation }
            );
        }

        closeOverlays();
    };
    const fitCanvasToViewport = (canvasWidth: number, canvasHeight: number) => {
        const viewport = canvasViewportRef.current;
        if (!viewport || canvasWidth <= 0 || canvasHeight <= 0) return;

        const padding = 24; // etwas Luft rundherum
        const availableWidth = Math.max(1, viewport.clientWidth - padding * 2);
        const availableHeight = Math.max(1, viewport.clientHeight - padding * 2);

        const fitZoom = Math.min(
            availableWidth / canvasWidth,
            availableHeight / canvasHeight
        );

        setZoom(clampZoom(fitZoom));
        setPanOffset({ x: 0, y: 0 });
    };
    const handleAddLayer = (type: 'image' | 'text' | 'drawings') => {
        switch(type){
            case 'drawings': {
                dispatchToolkitAction("create-draw-layer")
                break;
            }
            case 'image': {
                dispatchToolkitAction("create-image-layer")
                break;
            }
            case 'text': {
                dispatchToolkitAction("create-text-layer")
                break;
            }
        }
    };
    const handleExportImage = async (type: ExportFormat,scale: downloadScaleTypes='1x') => {
        if (!selectedImage) return;

        try {
            handleShowToast(downloadStartElementID);
            await downloadImageInFormat({
                image: selectedImage,
                layers: imageLayer,
                format: type,
                fileName:
                    selectedImage.title ||
                    selectedImage.file?.name ||
                    inputEditFromSidebar?.title ||
                    "image",
                exportFormat: scale
            });
        } catch (error) {
            handleShowToast(downloadFailedElementID)
        }
    };
    const handleHideSelectedLayer = () => {
        const newLayer: LayerProps [] = [];
        for(let currentLayer of imageLayer){
            if(selectedLayerIDs.includes(currentLayer.layerID)){
                currentLayer.hidden = true;
            }
            newLayer.push(currentLayer);
        }
        setImageLayer(newLayer);
    };
    const stopPickingColor = () => {
        setIsPickingBackgroundColor(false);
        setIsPickingBorderColor(false);
        setIsPickingDrawingColor(false);
        setIsPickingImageLayerBackgroundColor(false);
        setIsPickingImageLayerBorderColor(false);
        setIsPickingTextColor(false);
        setIsPickingTransparencyColor(false);
        setIsPickingTransparencyTransitionColor(false);
    };
    const handleAddGalleryImageAsLayer = async (
        index: number,
        img: ImageProps
    ) => {
        if (!img.file) return;

        const file = await resolveGalleryDragImageFile(img.file.name);
        if (!file) return;

        if (!selectedImage || selectedImage.src === "") {
            await createNewImageEntry(file);
            return;
        }

        const fullImg = await fileToImageProps(file);

        const canvas = canvasRef.current;
        if (!canvas) return;

        createImageLayerFromImage(
            fullImg,
            canvas.width / 2,
            canvas.height / 2,
            { height: 200 }
        );
    };

    //resize expand and crop handling with keyboard
    const resizeOverlayRectWithKeyboard = (
        corner: OverlayCorner,
        key: string,
        stepPx: number
    ) => {
        const activeBaseRect =
            overlayTarget === "layer"
                ? activeOverlayBaseLayerRect
                : activeOverlayBaseImageRect;

        if (!activeBaseRect) return;

        const dx = stepPx / Math.max(1, activeBaseRect.width);
        const dy = stepPx / Math.max(1, activeBaseRect.height);

        const setter =
            overlayTarget === "layer"
                ? setOverlayLayerRect
                : setOverlayImageRect;

        setter(prev => {
            let left = prev.x;
            let right = prev.x + prev.w;
            let top = prev.y;
            let bottom = prev.y + prev.h;

            const isLeft = corner.includes("w");
            const isRight = corner.includes("e");
            const isTop = corner.includes("n");
            const isBottom = corner.includes("s");

            if (key === "ArrowLeft") {
                if (isLeft) left -= dx;
                if (isRight) right -= dx;
            }

            if (key === "ArrowRight") {
                if (isLeft) left += dx;
                if (isRight) right += dx;
            }

            if (key === "ArrowUp") {
                if (isTop) top -= dy;
                if (isBottom) bottom -= dy;
            }

            if (key === "ArrowDown") {
                if (isTop) top += dy;
                if (isBottom) bottom += dy;
            }

            return ensureOverlayInBounds({
                x: left,
                y: top,
                w: right - left,
                h: bottom - top
            });
        });
    };
    const resizeExpandPaddingWithKeyboard = (
        corner: OverlayCorner,
        key: string,
        step: number
    ) => {
        if (!openExtandImage.active) return;

        const isLeft = corner.includes("w");
        const isRight = corner.includes("e");
        const isTop = corner.includes("n");
        const isBottom = corner.includes("s");

        const next = { ...openExtandImage.padding };

        if (key === "ArrowLeft") {
            if (isLeft) next.left += step;
            if (isRight) next.right -= step;
        }

        if (key === "ArrowRight") {
            if (isLeft) next.left -= step;
            if (isRight) next.right += step;
        }

        if (key === "ArrowUp") {
            if (isTop) next.top += step;
            if (isBottom) next.bottom -= step;
        }

        if (key === "ArrowDown") {
            if (isTop) next.top -= step;
            if (isBottom) next.bottom += step;
        }

        handleChangeExtendPadding({
            top: Math.max(0, next.top),
            right: Math.max(0, next.right),
            bottom: Math.max(0, next.bottom),
            left: Math.max(0, next.left)
        });
    };
    

    useEffect(() => {
        setinitialImageLists();
        return () => {
            if (expandPaddingFrameRef.current != null) {
                cancelAnimationFrame(expandPaddingFrameRef.current);
            }
        };
    }, []);
    useEffect(() => {
        const updateWindowSize = () => {
            const small = window.innerWidth < TOOLBAR_BREAKPOINT_WIDTH;

            setWindowSizeSmall(small);
        };

        updateWindowSize();

        window.addEventListener("resize", updateWindowSize);

        return () => {
            window.removeEventListener("resize", updateWindowSize);
        };
    }, []);
    useEffect(() => {
        if (historyOfEditedImages.length > 0) {
            setHistoryIndex(historyOfEditedImages.length - 1);
        }
    }, [historyOfEditedImages.length]);
    useEffect(() => {
        if (loadProjectDataActiveRef.current) {
            lastLayerDirtyRef.current = imageLayer;
            return;
        }

        if (lastLayerDirtyRef.current === null) {
            lastLayerDirtyRef.current = imageLayer;
            return;
        }

        if (lastLayerDirtyRef.current !== imageLayer) {
            lastLayerDirtyRef.current = imageLayer;
            markProjectEdited();
        }
    }, [imageLayer]);
    useEffect(() => {
        if (loadProjectDataActiveRef.current) return;
        if (!projectDirtyRef.current) return;
        if (!selectedImage?.file) return;

        clearAutosaveTimer();

        const snapshot = createProjectSnapshot();

        autosaveTimerRef.current = window.setTimeout(() => {
            if (loadProjectDataActiveRef.current) return;

            projectDirtyRef.current = false;
            queueProjectSave(snapshot,baseImageVariations);
        }, autosaveTimer);

        return () => {
            clearAutosaveTimer();
        };
    }, [projectDirtyRevision]);

    // Draw selected/hover image to the visible canvas (editing happens on the same canvas)
    useEffect(() => {
        const imgSrc = hoverPreview?.src ?? selectedImage?.src;
        const canvas = canvasRef.current;
        if (!imgSrc || !canvas) return;

        const drawToken = ++canvasDrawTokenRef.current;
        const img = new Image();

        img.onload = () => {
            if (drawToken !== canvasDrawTokenRef.current) return;

            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;

            const ctx = canvas.getContext("2d")!;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);

            fitCanvasToViewport(img.naturalWidth, img.naturalHeight);
        };

        img.src = imgSrc;

        if (imageLayer.length < 1 && !selectedLayer) {
            onChangeWorkOnBaseImage(true);
        }
    }, [selectedImage?.src, hoverPreview?.src]);
    useEffect(() => {
        //Allow zooming with mouse and strg
        const wrapper = canvasWrapperRef.current;
        if (!wrapper) return;

        const handleWheel = (e: WheelEvent) => {
            if (!e.ctrlKey) return;
            e.preventDefault();

            setZoom((prev) => clampZoom(prev + (e.deltaY < 0 ? 0.1 : -0.1)));
        };

        wrapper.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            wrapper.removeEventListener("wheel", handleWheel);
        };
    }, []);
    useEffect(() => {
        const onKeyUp = (e: KeyboardEvent) => {
            if (keyboardResizeKeyCodeRef.current !== e.code) return;

            keyboardResizeActiveRef.current = false;
            keyboardResizeKeyCodeRef.current = null;
        };

        window.addEventListener("keyup", onKeyUp);

        return () => {
            window.removeEventListener("keyup", onKeyUp);
        };
    }, []);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (isTypingTarget()) return;

            const key = e.key.toLowerCase();
            const isCtrlOrCmd = e.ctrlKey || e.metaKey;
            const isPlusKey =
                e.key === "+" ||
                e.key === "=";

            const isMinusKey =
                e.key === "-" ||
                e.key === "_";

            if (isCtrlOrCmd && e.shiftKey && key === "a") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                deselectAllByShortcut();
                return;
            }

            if (isCtrlOrCmd && !e.shiftKey && key === "a") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                selectAllLayersByShortcut();
                return;
            }

            if (isCtrlOrCmd && !e.shiftKey && key === "s") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                exportPngByShortcut();
                return;
            }
            if (isCtrlOrCmd && key === "0") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                resetCanvasView();
                return;
            }

            if (isCtrlOrCmd && isPlusKey) {
                e.preventDefault();
                pendingShortcutRef.current = null;
                handleZoomIn();
                return;
            }

            if (isCtrlOrCmd && isMinusKey) {
                e.preventDefault();
                pendingShortcutRef.current = null;
                handleZoomOut();
                return;
            }

            if (isCtrlOrCmd && e.shiftKey && key === "m") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                mergeSelectedLayersByShortcut();
                return;
            }

            if (isCtrlOrCmd && e.altKey && key === "b") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                writeSelectedLayersToBaseByShortcut();
                return;
            }
            if (isCtrlOrCmd && e.altKey && e.shiftKey && key === "h") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                handleHideAllLayers();
                return;
            }

            if (isCtrlOrCmd && e.altKey && e.shiftKey && key === "u") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                handleUnhideAllLayers();
                return;
            }
            if (isCtrlOrCmd && e.shiftKey && key === "h") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                hideSelectedLayerByShortcut();
                return;
            }
            if (isCtrlOrCmd && !e.shiftKey && key === "z") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                undoCurrentTarget();
                return;
            }

            if (
                (isCtrlOrCmd && key === "y") ||
                (isCtrlOrCmd && e.shiftKey && key === "z")
            ) {
                e.preventDefault();
                pendingShortcutRef.current = null;
                redoCurrentTarget();
                return;
            }

            if (isCtrlOrCmd && e.altKey && key === "r") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                resetCurrentTarget();
                return;
            }

            if (isCtrlOrCmd && key === "r") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                openRotateModeForCurrentTarget();
                return;
            }

            if (rotateMode &&!isCtrlOrCmd &&!e.altKey &&(e.key === "ArrowLeft" || e.key === "ArrowRight")) {
                e.preventDefault();
                e.stopPropagation();

                const step = e.shiftKey ? 5 : 1;
                const direction = e.key === "ArrowLeft" ? -1 : 1;

                rotateCurrentTargetBy(direction * step);
                return;
            }

            if (rotateMode && e.key === "Enter") {
                e.preventDefault();
                void handleRotateSave(rotationDraft);
                return;
            }
            if (cropMode && e.key === "Enter") {
                e.preventDefault();
                void handleCropSave();
                return;
            }
            if (openExtandImage.active && e.key === "Enter") {
                e.preventDefault();
                void saveExtendImage();
                return;
            }

            if (isCtrlOrCmd && e.altKey && key === "c") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                openCropModeForCurrentTarget();
                return;
            }
            if (isCtrlOrCmd && e.altKey && e.shiftKey && e.code === "KeyE") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                openExpandTransparentForCurrentTarget();
                return;
            }
            if (e.altKey && key === "v") {
                e.preventDefault();
                pendingShortcutRef.current = null;
                openExpandWithVariations(selectedLayer ? 'layer' : 'base');
                return;
            }
            const keyboardCorner = getKeyboardCornerFromCode(e.code);
            if (keyboardCorner && !isCtrlOrCmd && !e.altKey) {
                e.preventDefault();
                keyboardResizeCornerRef.current = keyboardCorner;
                keyboardResizeActiveRef.current = true;
                keyboardResizeKeyCodeRef.current = e.code;
                return;
            }

            if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key) &&!isCtrlOrCmd &&!e.altKey &&!rotateMode &&keyboardResizeActiveRef.current) {
                e.preventDefault();
                e.stopImmediatePropagation();

                const step = getKeyboardStep(e);
                const corner = keyboardResizeCornerRef.current;

                if (openExtandImage.active) {
                    resizeExpandPaddingWithKeyboard(corner, e.key, step);
                    return;
                }

                if (cropMode) {
                    resizeOverlayRectWithKeyboard(corner, e.key, step);
                    return;
                }

                if (selectedLayerIDs.length === 1 && selectedLayer) {
                    resizeSelectedLayerWithKeyboard(corner, e.key, step);
                    return;
                }
            }
            

            if (isCtrlOrCmd && key === "f") {
                e.preventDefault();
                pendingShortcutRef.current = "flip";
                return;
            }

            if (pendingShortcutRef.current === "flip" &&["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
                e.preventDefault();

                if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                    flipCurrentTarget("horizontal");
                } else {
                    flipCurrentTarget("vertical");
                }

                pendingShortcutRef.current = null;
                return;
            }
        };

        window.addEventListener("keydown", onKey);

        return () => {
            window.removeEventListener("keydown", onKey);
        };
    }, [
        selectedImage,
        selectedLayer,
        selectedLayerIDs,
        imageLayer,
        rotateMode,
        rotationDraft,
        openExtandImage.active,
        inputEditFromSidebar,
        overlayTarget,
        cropMode,
        openExtandImage.active,
        openExtandImage.padding,
        selectedLayer,
        selectedLayerIDs,
        imageLayer,
        overlayTarget,
        flipCanvasHorizontal,
        flipCanvasVertical,
        openCropCanvas,
        handleZoomIn,
        handleZoomOut,
        imageLayer,
        selectedImage,
        selectedLayerIDs
    ]);
    useEffect(()=>{
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                closeOverlays();
                e.preventDefault();
                pendingShortcutRef.current = null;
                setContextMenu(prev => ({ ...prev, visible: false }));
                return;
            }
        };
        window.addEventListener("keydown", onKey);

        return () => {
            window.removeEventListener("keydown", onKey);
        };
    },[]);
    useEffect(() => {
    const wrapper = canvasWrapperRef.current;
        if (!wrapper) return;

        const onMiddleMouseDown = (e: MouseEvent) => {
            if (e.button !== 1) return;

            e.preventDefault();
            e.stopPropagation();

            beginMiddleMouseDrag(e);
        };

        const preventMiddleMouseAutoScroll = (e: MouseEvent) => {
            if (e.button === 1) {
                e.preventDefault();
            }
        };

        wrapper.addEventListener("mousedown", onMiddleMouseDown, true);
        wrapper.addEventListener("auxclick", preventMiddleMouseAutoScroll, true);

        return () => {
            wrapper.removeEventListener("mousedown", onMiddleMouseDown, true);
            wrapper.removeEventListener("auxclick", preventMiddleMouseAutoScroll, true);
        };
    }, [
        selectedImage,
        cropMode,
        rotateMode,
        openExtandImage.active,
        editingTextLayerId,
        imageLayer,
        selectedLayerIDs,
        panOffset,
        canvasDisplayRect
    ]);
    useEffect(()=>{
        setSelectedVariationIndex(null);
        setActiveToolkitMode("pointer");
        stopPickingColor();
        if(selectedLayer || selectedLayerIDs.length > 0){
            setOverlayTarget("layer");
            onChangeWorkOnBaseImage(false);
        }
        else{
            setOverlayTarget("image");
            onChangeWorkOnBaseImage(true);
        }
    },[selectedLayer,selectedLayerIDs]);
    useEffect(()=>{
        if(isClosingOverlays){
            setIsClosingOverlays(false);
        }
    },[isClosingOverlays]);
    useEffect(() => {
        const isSelectionTool =
            activeToolkitMode === "select-rect" ||
            activeToolkitMode === "select-oval" ||
            activeToolkitMode === "select-free";

        const drawingLayerSelected =
            selectedLayerIDs.length === 1 && selectedLayer?.type === "drawings";

        const drawingActionActive =
            !!selectedDrawingTool &&
            (
                selectedDrawingTool === "brush" ||
                selectedDrawingTool === "eraser" ||
                selectedDrawingTool === "fill" ||
                selectedDrawingTool.startsWith("shape-")
            );

        if (!drawingLayerSelected) return;

        if (drawingActionActive && (activeToolkitMode === "hand" || isSelectionTool)) {
            resetTransientTools();
            setActiveToolkitMode("pointer");
        }
    }, [selectedLayer, selectedLayerIDs, selectedDrawingTool, activeToolkitMode]);
    useEffect(() => {
        if (!selectedLayer || selectedLayer.type !== "text" || !selectedLayer.textData) return;

        const textData = selectedLayer.textData;

        setPickedTextColor(textData.fontColor || "#ffffff");
        setPickedBackgroundColor(textData.backgroundColor || "#ffffff");
        setPickedBorderColor(textData.borderColor || "#ffffff");

        setTextOpacity(Math.round((textData.textOpacity ?? 1) * 100));
        setBackgroundOpacityTextLayer(Math.round((textData.backgroundOpacity ?? 0) * 100));
        setBorderOpacityTextLayer(Math.round((textData.borderOpacity ?? 0) * 100));

        setSelectedTextSize(Number((textData.fontSize || "16px").replace("px", "")) || 16);
        setSelectedFontType(textData.fontFamily || "Arial");
        setSelectedBorderWidth(textData.borderWidth ?? 0);
        setSelectedBorderType(textData.borderStyle ?? "none");
        setSelectedTextContent(textData.text || "");

        setTextIsBold(textData.style?.includes("bold") ?? false);
        setTextIsItalic(textData.style?.includes("italic") ?? false);
        setTextIsUnderline(textData.style?.includes("underline") ?? false);
    }, [selectedLayer]);
    useEffect(() => {
        if (!selectedLayer || (selectedLayer.type !== "image" && selectedLayer.type !== "ai-variation")) return;

        const style = selectedLayer.imageLayerStyle ?? {};

        setSelectedImageLayerImage(selectedLayer.imageEdit || selectedLayer.imageData || null);
        setSelectedImageLayerOpacity(Math.round((style.imageOpacity ?? 1) * 100));

        setSelectedImageLayerBorderColor(style.borderColor || "#ffffff");
        setSelectedImageLayerBorderOpacity(Math.round((style.borderOpacity ?? 1) * 100));
        setSelectedImageLayerBorderWidth(style.borderWidth ?? 0);
        setSelectedImageLayerBorderType(style.borderStyle ?? "none");

        setSelectedImageLayerBackgroundColor(style.backgroundColor || "#ffffff");
        setSelectedImageLayerBackgroundOpacity(Math.round((style.backgroundOpacity ?? 1) * 100));
    }, [selectedLayer]);
    useEffect(() => {
        //save image if studio is closed
        const saveBeforeLeave = () => {
            if (!projectDirtyRef.current) return;

            const snapshot = createProjectSnapshot();
            queueProjectSave(snapshot,baseImageVariations);
        };

        window.addEventListener("pagehide", saveBeforeLeave);
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") {
                saveBeforeLeave();
            }
        });

        return () => {
            window.removeEventListener("pagehide", saveBeforeLeave);
        };
    }, [selectedImage, imageLayer, createdAt]);
    useEffect(() => {
        if (
            editingTextLayerId != null &&
            (!selectedLayer || selectedLayer.layerID !== editingTextLayerId || selectedLayerIDs.length !== 1)
        ) {
            setEditingTextLayerId(null);
        }
    }, [selectedLayer, selectedLayerIDs, editingTextLayerId]);
    useEffect(()=> {
        if(isAnyColorPickingActive){
            setActiveToolkitMode(null);
        }
    },[isAnyColorPickingActive]);
    useEffect(() => {
        const hasSelectedVariation =
            selectedVariationIndex !== null &&
            selectedVariationIndex >= 0;

        setIsBaseImageVariationSelected(
            hasSelectedVariation &&
            !selectedLayer &&
            selectedLayerIDs.length < 1
        );
    }, [selectedVariationIndex, selectedLayer, selectedLayerIDs]);
    useEffect(() => {
        setIsMarqueeArea(!!layerAreaSelection);
        onChangeExpandImageVariationsActive(false);
    }, [layerAreaSelection]);
    useEffect(() => {
        let cancelled = false;
        const updateAiEditReference = async () => {
            setSelectedVariationIndex(null);
            
            if (layerAreaSelection) {
                const marqueeLayer = await buildAiReferenceLayerFromSelection();
                //set reference for marquee to base image if marking not on layer
                if(!selectedLayer){
                    onChangeWorkOnBaseImage(true);
                }
                else{
                    onChangeWorkOnBaseImage(false);
                }
                if (!cancelled && marqueeLayer) {
                    await convertToAiReferenceData(true, marqueeLayer);
                }
                return;
            }
            //catch base image selection
            if(workOnBaseImage && selectedImage){
                await convertToAiReferenceData(false, selectedImage);
                return;
            }

            if (selectedLayerIDs.length === 1 && selectedLayer) {
                const layerAsImage = await buildAiReferenceLayerFromLayer(selectedLayer);
                let createNewLayer = selectedLayer.type === 'text' || selectedLayer.type === 'drawings';
                if (!cancelled && layerAsImage) {
                    await convertToAiReferenceData(createNewLayer,layerAsImage);
                }
                return;
            }
            if(selectedLayerIDs.length > 1){
                //build new layer
                //avoid selecting hidden layer
                const selectedLayers = imageLayer.filter((l) =>
                    selectedLayerIDs.includes(l.layerID) &&
                    !l.hidden
                );
                if (selectedLayers.length < 2) return;
                const [mergedLayer, _] = await mergeLayerAndGetLayerWithSelectionIds(selectedLayers);

                if (!cancelled && mergedLayer) {
                    await convertToAiReferenceData(true,mergedLayer);
                }
                return;
            }
            if (!cancelled && selectedImage) {
                await convertToAiReferenceData(false, selectedImage);
            }
        };

        void updateAiEditReference();

        return () => {
            cancelled = true;
        };
    }, [
        layerAreaSelection,
        selectedLayer,
        selectedLayerIDs,
        selectedImage,
        workOnBaseImage
    ]);
    const selectedTargetKey =
        selectedLayerIDs.length === 1
            ? `layer-${selectedLayerIDs[0]}`
            : "base";

    useEffect(() => {
        setLayerAreaSelection(null);
        setIsMarqueeArea(false);
    }, [selectedTargetKey]);
    
    useEffect(()=>{
        if(!expandImageVariationsActive){
            setExpandPadding(ZERO_EXTEND_PADDING);
        }
    },[expandImageVariationsActive]);

    useEffect(() => {
        if(!showGallery && selectedImage){
            //reisze canvas after gallery show
            resetCanvasView();
        }
    },[showGallery]);

    useLayoutEffect(() => {
        let frameA = 0;
        let frameB = 0;

        const update = () => {
            cancelAnimationFrame(frameA);
            cancelAnimationFrame(frameB);

            frameA = requestAnimationFrame(() => {
                frameB = requestAnimationFrame(() => {
                    updateCanvasDisplayRect();
                });
            });
        };

        update();

        const resizeObserver = new ResizeObserver(update);

        if (canvasWrapperRef.current) resizeObserver.observe(canvasWrapperRef.current);
        if (canvasViewportRef.current) resizeObserver.observe(canvasViewportRef.current);
        if (canvasRef.current) resizeObserver.observe(canvasRef.current);
        if (imageStageTransformRef.current) resizeObserver.observe(imageStageTransformRef.current);

        window.addEventListener("resize", update);

        return () => {
            cancelAnimationFrame(frameA);
            cancelAnimationFrame(frameB);
            resizeObserver.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [
        zoom,
        panOffset.x,
        panOffset.y,
        selectedImage?.src,
        hoverPreview?.src,
        openExtandImage.active,
        openExtandImage.padding.top,
        openExtandImage.padding.right,
        openExtandImage.padding.bottom,
        openExtandImage.padding.left
    ]);

    const overlayPortal = OverlayPortal({
        canvasDisplayRect: canvasDisplayRect,
        canvasRef: canvasRef,
        onResolveGalleryImageFile:resolveGalleryDragImageFile,
        canvasWrapperRef: canvasWrapperRef,
        overlayRect: overlayTarget === "image" ? overlayImageRect : overlayLayerRect,
        activeOverlayBaseRect: overlayTarget === "image" ? activeOverlayBaseImageRect : activeOverlayBaseLayerRect,
        activeToolkitTool: activeToolkitMode,
        imageLayer: hoverPreviewSource === "gallery" ? [] : imageLayer,
        selectedLayer: selectedLayer,
        selectedLayerIDs: selectedLayerIDs,
        marqueeSelectionPreview: marqueeSelectionPreview,
        activeLayerEditId: activeLayerEditId,
        layerAreaSelection: layerAreaSelection,
        selectionPreview: selectionPreview,
        overlayTarget: overlayTarget,
        rotateMode: rotateMode,
        cropMode: cropMode,
        rotationDraft: rotationDraft,
        isPickingColor: isAnyColorPickingActive,
        onPickSurfaceColor: handleSetSelectedColor,
        drawingCursorPreview: drawingCursorPreview,
        drawingStrokePreview: drawingStrokePreview,
        shapePreview: shapePreview,
        selectedDrawingTool: selectedDrawingTool,
        editingTextLayerId: editingTextLayerId,
        editingTextValue: editingTextValue,
        onStartTextEdit: startTextEdit,
        onChangeEditingText: changeEditingText,
        onCommitTextEdit: commitTextEdit,
        setOverlayRect: overlayTarget === "image" ? setOverlayImageRect : setOverlayLayerRect,
        updatePointerSelection: updatePointerSelection,
        beginDrawingStroke: beginDrawingStroke,
        beginLayerTransform: beginLayerTransform,
        beginPointerMarqueeSelection: beginPointerMarqueeSelection,
        beginSelectionDrag: beginSelectionDrag,
        openRotate: openRotate,
        onIsRotating: setIsRotating,
        closeOverlays: closeOverlays,
        handleCropSave: handleCropSave,
        handleRotateSave: handleRotateSave,
        setContextMenu: setContextMenu,
        onSelectedLayerChange: setSelectedLayer,
        onSelectedLayerIDsChange: setSelectedLayerIDs,
        setLayerAreaSelection: setLayerAreaSelection,
        onDropImageOnSelectedImageLayer: handleDropImageOnSelectedImageLayer,
        onDropImageOnCanvas: handleDropImageOnCanvas,
        onDropGalleryImageOnCanvas: handleDropGalleryImageOnCanvas,
        onDropGalleryImageOnSelectedImageLayer: handleDropGalleryImageOnSelectedImageLayer,
        openExtandImage: openExtandImage,
        onChangeExtendPadding: handleChangeExtendPadding,
        onCancelExtendImage: closeExtendImage,
        onSaveExtendImage: saveExtendImage,
    });

  return (
    <>
        
        
        {showExpandVariationModal && (
            <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ zIndex: 40000, background: "rgba(0,0,0,0.45)"}}
            >
                <EffectThemeContainer 
                    containerClass="p-3"
                    children={
                        <>
                            <h5>{t('expand.title')}</h5>
                            <p>
                                {t('expand.description')}
                            </p>

                            <p>
                                <strong>{t('expand.promptLabel')}</strong> {inputAiEditFromSidebar?.prompt_positive || t('expand.noPrompt')}
                            </p>

                            <p>
                                <strong>{t('expand.variationsLabel')}</strong> {inputAiEditFromSidebar?.numberGenerations ?? t('expand.currentSetting')}
                            </p>

                            <div className="d-flex gap-2 justify-content-end">
                                <EffectThemeButton onClick={() => setShowExpandVariationModal(false)}>
                                    {t('expand.adjustSettings')}
                                </EffectThemeButton>

                                <EffectThemeButton
                                    onClick={() => {
                                        setShowExpandVariationModal(false);
                                        void handleGenerateVariations(
                                            'expanding',
                                            openExtandImage.target === "base" ? "base" : "layer"
                                        );
                                    }}
                                >
                                    <AILogo/> {t('expand.getVariations')}
                                </EffectThemeButton>
                            </div>
                        </>
                    }
                />
            </div>
        )}
        {isMobile && <DesktopOnlyNotice tool="imageEditor" />}
        {overlayPortal}

        {loadProjectDataActive ? <LoadingOverlay /> : null}
            <div className="container-fluid h-100 pb-2">
                <DownloadStartToast
                    id={downloadStartElementID}
                />
                <DownloadFailedToast
                    id={downloadFailedElementID}
                />
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0' style={{minHeight:'42px'}}>
                    <div className='col p-2 px-0'>
                        <Link 
                            to="/executions" 
                            className="btn btn-outline-secondary btn-sm"
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('page.back')}
                            />
                        </Link>
                    </div>
                    <div className="col-auto"  style={{fontSize:'30px'}}>
                        {aiGenerationsState
                            ? aiGenerationsState === 'send'
                                ? <p className='m-0'>{stateIconSend} {t('state.sending')}</p>
                                : aiGenerationsState === 'waiting'
                                    ? <p className='m-0'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                                    : aiGenerationsState === 'error'
                                        ? <p className='m-0'>{stateIconError} {t('state.error')}</p>
                                        : aiGenerationsState === 'done'
                                            ? <p className='m-0'>{stateIconDone} {t('state.done')}</p>
                                            : <></>
                            : <></>
                        }
                    </div>
                    <div className="col"></div>
                </div>
                <div className='row flex-grow-1 overflow-hidden pb-2'>
                    <div className='col-2 h-100 p-0 overflow-y-auto'>
                        {settingsSideBar}
                    </div>
                    <div className='col-8 my-2 h-100'>
                        <div className='row h-100 w-100'>
                            <div className='d-flex flex-column h-100 w-100' style={{zIndex: 100}}>
                                <div
                                    ref={canvasContextAreaRef}
                                    className="flex-grow-1 overflow-hidden w-100 m-0 p-0 text-center d-flex flex-column position-relative"
                                >
                                    <EffectThemeContainer
                                        containerClass="flex-grow-1 overflow-hidden w-100 m-0 p-2 text-center d-flex flex-column position-relative"
                                        children={
                                            <>
                                                <div
                                                    className="flex-grow-1 position-relative w-100"
                                                    style={{
                                                        minHeight: 0,
                                                        height: "100%",
                                                        width: "100%",
                                                        overflow: isExpandActive  ? "auto" : "hidden"
                                                    }}
                                                    ref={canvasViewportRef}
                                                >
                                                    <div
                                                        ref={canvasWrapperRef}
                                                        className="position-relative w-100 h-100 d-flex justify-content-center align-items-center"
                                                        style={{
                                                            minWidth: getExpandScrollMinSize().width,
                                                            minHeight: getExpandScrollMinSize().height,
                                                            justifyContent: isExpandActive  ? "flex-start" : "center",
                                                            alignItems: isExpandActive  ? "flex-start" : "center"
                                                        }}
                                                        onDragOver={(e) => {
                                                            if (selectedImage?.src) return;
                                                            const galleryThumbnailName = e.dataTransfer.getData(
                                                                "application/x-sengi-gallery-thumbnail"
                                                            );
                                                            const fileItem = Array.from(e.dataTransfer.items ?? []).find(
                                                                item => item.kind === "file"
                                                            );
                                                            if (!galleryThumbnailName && !fileItem) return;
                                                            e.preventDefault();
                                                            e.dataTransfer.dropEffect = "copy";
                                                        }}
                                                        onDrop={async (e) => {
                                                            if (selectedImage?.src) return;
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            const galleryThumbnailName = e.dataTransfer.getData("application/x-sengi-gallery-thumbnail");
                                                            if (galleryThumbnailName) {
                                                                await handleDropGalleryImageOnCanvas(galleryThumbnailName, 0, 0);
                                                                return;
                                                            }
                                                            const file = e.dataTransfer.files?.[0];
                                                            if (!file || !file.type.startsWith("image/")) return;
                                                            await handleDropBaseImage(file);
                                                        }}
                                                        onMouseDown={(e) => {
                                                            if (e.button === 1) {
                                                                beginMiddleMouseDrag(e);
                                                            }
                                                        }}
                                                    >
                                                        {(selectedImage || hoverPreview) ? (
                                                            <div
                                                                ref={imageStageTransformRef}
                                                                className="position-relative image-stage-transform"
                                                                onTransitionEnd={updateCanvasDisplayRect}
                                                                style={{
                                                                    padding: `${getExpandLayoutPadding().top}px ${getExpandLayoutPadding().right}px ${getExpandLayoutPadding().bottom}px ${getExpandLayoutPadding().left}px`,
                                                                    transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
                                                                    transformOrigin: "center center",
                                                                    transition: isPanning || openExtandImage.active ? "none" : "transform 0.08s ease-out",
                                                                    display: "inline-block",
                                                                    boxSizing: "content-box"
                                                                }}
                                                            >
                                                                <div
                                                                    className="position-relative image-stage"
                                                                    style={{
                                                                        display: "inline-block"
                                                                    }}
                                                                >
                                                                    {!isRotatePreviewActive && (
                                                                        <div
                                                                            aria-hidden="true"
                                                                            style={{
                                                                                position: "absolute",
                                                                                inset: 0,
                                                                                pointerEvents: "none",
                                                                                zIndex: 0,
                                                                                backgroundColor: hoverPreviewSource === null
                                                                                        ? SengiColors.sng_pearl
                                                                                        : "transparent",

                                                                                backgroundImage: hoverPreviewSource === null
                                                                                    ? `
                                                                                        linear-gradient(45deg, ${SengiColors.sng_disabled} 25%, transparent 25%),
                                                                                        linear-gradient(-45deg, ${SengiColors.sng_disabled} 25%, transparent 25%),
                                                                                        linear-gradient(45deg, transparent 75%, ${SengiColors.sng_disabled} 75%),
                                                                                        linear-gradient(-45deg, transparent 75%, ${SengiColors.sng_disabled} 75%)
                                                                                    `
                                                                                    : "transparent",
                                                                                backgroundSize: `${transparencyTileSize}px ${transparencyTileSize}px`,
                                                                                backgroundPosition: `
                                                                                    0 0,
                                                                                    0 ${transparencyTileSize / 2}px,
                                                                                    ${transparencyTileSize / 2}px -${transparencyTileSize / 2}px,
                                                                                    -${transparencyTileSize / 2}px 0
                                                                                `
                                                                            }}
                                                                        />
                                                                    )}

                                                                    <canvas
                                                                        ref={canvasRef}
                                                                        onMouseDown={(e) => {
                                                                            if (e.button === 1) {
                                                                                beginMiddleMouseDrag(e);
                                                                                return;
                                                                            }

                                                                            if (isAnyColorPickingActive) {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                handleSetSelectedColor(e);
                                                                                return;
                                                                            }

                                                                            handleCanvasMouseDown(e);
                                                                        }}
                                                                        onMouseMove={handleCanvasMouseMove}
                                                                        onMouseUp={handleCanvasMouseUp}
                                                                        onMouseLeave={handleCanvasMouseUp}
                                                                        style={{
                                                                            position: "relative",
                                                                            zIndex: 1,
                                                                            display: "block",
                                                                            maxWidth: "none",
                                                                            maxHeight: "none",
                                                                            width: "auto",
                                                                            height: "auto",
                                                                            background: "transparent",
                                                                            cursor: isAnyColorPickingActive
                                                                                ? "crosshair"
                                                                                : activeToolkitMode === "hand"
                                                                                ? (isPanning ? "grabbing" : "grab")
                                                                                : activeToolkitMode === "pointer"
                                                                                ? "default"
                                                                                : "default"
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ) : (
                                                                <>
                                                                <ContextMenuEmptyBase
                                                                    wrapperRef={canvasContextAreaRef}
                                                                    contextMenu={contextMenu}
                                                                    setContextMenu={setContextMenu}
                                                                    uploadBaseImage={createNewImageEntry}
                                                                />
                                                                    <div
                                                                        className="h3 opacity-25"
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            minHeight: "300px",
                                                                            display: "flex",
                                                                            flexDirection: "column",
                                                                            alignItems: "center",
                                                                            justifyContent: "center"
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon
                                                                            icon={faImage}
                                                                            size="2xl"
                                                                            className="me-2"
                                                                        />
                                                                        {t('page.noImageSelected')}
                                                                        <p className="h4">
                                                                            <Trans t={t} i18nKey="hint.createOrUpload" components={[<strong />, <strong />]} />
                                                                        </p>
                                                                    </div>
                                                                </>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                                {
                                                    selectedImage && selectedImage.src !== ""
                                                    ?   <div
                                                            className="flex-shrink-0 pt-2 d-flex justify-content-center align-items-start w-100"
                                                            style={{
                                                                height: `${toolkitReservedHeight}px`,
                                                                minHeight: `${toolkitReservedHeight}px`,
                                                                maxHeight: `${toolkitReservedHeight}px`,
                                                                minWidth: 0,
                                                                maxWidth: "100%",
                                                                overflow: "hidden"
                                                            }}
                                                        >
                                                            <BasicImageToolKit
                                                                activeMode={activeToolkitMode}
                                                                onChangeMode={setSafeToolkitMode}
                                                                hasSelectedLayer={selectedLayerIDs.length > 0}
                                                                hasMultipleSelectedLayers={selectedLayerIDs.length > 1}
                                                                selectedLayerType={
                                                                    selectedLayerIDs.length === 1
                                                                        ? selectedLayer?.type ?? null
                                                                        : null
                                                                }
                                                                layers={imageLayer} 
                                                                canUndo={canUndo}
                                                                canRedo={canRedo}
                                                                onZoomIn={handleZoomIn}
                                                                onZoomOut={handleZoomOut}
                                                                onExportImage={handleExportImage}
                                                                image={selectedImage}
                                                                canvasRef={canvasRef}
                                                                downloadFormatList={downloadFormats}
                                                                upscaleCosts={imageEditCosts ? {
                                                                    upscale2x: imageEditCosts.upscale2x,
                                                                    upscale4x: imageEditCosts.upscale4x
                                                                } : null}
                                                                isPickingTransparencyColor={isPickingTransparencyColor}
                                                                onToggleTransparencyColorPicker={() => void startGlobalColorPicker("transparencyColor")}
                                                                colorPreviewList={coloPreviewList}
                                                                onChangeColorPreviewList={setColoPreviewList}
                                                                onDeleteSelectedLayers={() => dispatchToolkitAction("delete-selected-layers")}
                                                                onCreateDrawLayer={() => dispatchToolkitAction("create-draw-layer")}
                                                                onCreateTextLayer={() => dispatchToolkitAction("create-text-layer")}
                                                                onCreateImageLayer={() => dispatchToolkitAction("create-image-layer")}
                                                                onCropSelectedLayer={() => dispatchToolkitAction("crop-selected-layer")}
                                                                onRotateSelectedLayer={() => dispatchToolkitAction("rotate-selected-layer")}
                                                                onFlipHorizontalSelectedLayer={() => dispatchToolkitAction("flip-horizontal-layer")}
                                                                onFlipVerticalSelectedLayer={() => dispatchToolkitAction("flip-vertical-layer")}
                                                                onMergeSelectedWithPrevious={() => dispatchToolkitAction("merge-selected-with-previous")}
                                                                onMergeAllSelectedLayers={() => dispatchToolkitAction("merge-all-selected-layers")}
                                                                onReset={handleReset}
                                                                onUndo={handleUndo}
                                                                onRedo={handleRedo}
                                                                windowSizeSmall={windowSizeSmall}
                                                                onToggleCopySelectedArea={copySelectedAreaFromLayer}
                                                                onToggleCropSelectedArea={extractSelectedAreaToNewLayer}
                                                                onToggleDeleteSelectedArea={deleteSelectedAreaFromLayer}
                                                                onToggleDuplicateSelectedLayer={duplicateSelectedLayer}
                                                                selectedArea={layerAreaSelection}
                                                                selectedDrawingColor={pickedDrawingColor}
                                                                selectedBrushSize={selectedBrushSize}
                                                                selectedBrushHardness={selectedBrushHardness}
                                                                selectedBrushOpacity={selectedBrushOpacity}
                                                                selectedDrawingTool={selectedDrawingTool}
                                                                isPickingDrawingColor={isPickingDrawingColor}
                                                                onChangeSelectedDrawingColor={setPickedDrawingColor}
                                                                onChangeSelectedBrushSize={setSelectedBrushSize}
                                                                onChangeSelectedBrushHardness={setSelectedBrushHardness}
                                                                onChangeSelectedBrushOpacity={setSelectedBrushOpacity}
                                                                onChangeSelectedDrawingTool={setSelectedDrawingTool}
                                                                onToggleDrawingColorPicker={() => void startGlobalColorPicker("drawingColor")}
                                                                onToggleTextColorPicker={() => void startGlobalColorPicker("textColor")}
                                                                onToggleBackgroundColorPicker={() => void startGlobalColorPicker("backgroundColorText")}
                                                                onToggleBorderColorPicker={() => void startGlobalColorPicker("borderColorText")}
                                                                onToggleImageLayerBackgroundColorPicker={() => void startGlobalColorPicker("backgroundColorImage")}
                                                                onToggleImageLayerBorderColorPicker={() => void startGlobalColorPicker("borderColorImage")}
                                                                fontTypesList={fontTypesList}
                                                                borderStyleList={borderStyleList}
                                                                borderWidthList={borderWidthList}
                                                                textSizesList={textSizesList}

                                                                isPickingTextColor={isPickingTextColor}
                                                                isPickingBackgroundColor={isPickingBackgroundColor}
                                                                isPickingBorderColor={isPickingBorderColor}

                                                                textIsBold={textIsBold}
                                                                textIsItalic={textIsItalic}
                                                                textIsUnderline={textIsUnderline}

                                                                pickedTextColor={pickedTextColor}
                                                                pickedBorderColor={pickedBorderColor}
                                                                pickedBackgroundColor={pickedBackgroundColor}
                                                                onExpandSelectedLayerTransparent={openExpandLayerTransparent}
                                                                textOpacity={textOpacity}
                                                                borderOpacity={borderOpacityTextLayer}
                                                                backgroundOpacity={backgroundOpacityTextLayer}
                                                                selectedTextSize={selectedTextSize}
                                                                selectedFontType={selectedFontType}
                                                                selectedBorderWidth={selectedBorderWidth}
                                                                selectedBorderType={selectedBorderType}

                                                                onSelectTextColor={(color) => {
                                                                    setPickedTextColor(color);
                                                                    setSelectedTextColor(color);
                                                                }}
                                                                onSelectBackgroundColor={(color) => {
                                                                    setPickedBackgroundColor(color);
                                                                    setSelectedTextBackgroundColor(color);
                                                                }}
                                                                onSelectBorderColor={(color) => {
                                                                    setPickedBorderColor(color);
                                                                    setSelectedTextBorderColor(color);
                                                                }}

                                                                onChangeTextColorTransparency={(alpha) => {
                                                                    setTextOpacity(alpha);
                                                                    setSelectedTextOpacity(alpha);
                                                                }}
                                                                onChangeBackgroundColorTransparency={(alpha) => {
                                                                    setBackgroundOpacityTextLayer(alpha);
                                                                    setSelectedTextBackgroundOpacity(alpha);
                                                                }}
                                                                onChangeBorderColorTransparency={(alpha) => {
                                                                    setBorderOpacityTextLayer(alpha);
                                                                    setSelectedTextBorderOpacity(alpha);
                                                                }}

                                                                onSelectTextSize={(size) => {
                                                                    setSelectedTextSize(size);
                                                                    applySelectedTextSize(size);
                                                                }}
                                                                onSelectFontType={(font) => {
                                                                    setSelectedFontType(font);
                                                                    setSelectedTextFontFamily(font);
                                                                }}
                                                                onSelectBorderWidth={(width) => {
                                                                    setSelectedBorderWidth(width);
                                                                    setSelectedTextBorderWidth(width);
                                                                }}
                                                                onSelectBorderType={(type) => {
                                                                    setSelectedBorderType(type);
                                                                    setSelectedTextBorderStyle(type as "none" | "solid" | "dashed" | "dotted");
                                                                }}

                                                                setTextStyleIsBold={(enabled) => {
                                                                    setTextIsBold(enabled);
                                                                    toggleSelectedTextStyle("bold", enabled);
                                                                }}
                                                                setTextStyleIsItalic={(enabled) => {
                                                                    setTextIsItalic(enabled);
                                                                    toggleSelectedTextStyle("italic", enabled);
                                                                }}
                                                                setTextStyleIsUnderline={(enabled) => {
                                                                    setTextIsUnderline(enabled);
                                                                    toggleSelectedTextStyle("underline", enabled);
                                                                }}
                                                                selectedImageLayerImage={selectedImageLayerImage}
                                                                selectedImageLayerOpacity={selectedImageLayerOpacity}

                                                                selectedImageLayerBorderColor={selectedImageLayerBorderColor}
                                                                selectedImageLayerBorderOpacity={selectedImageLayerBorderOpacity}
                                                                selectedImageLayerBorderWidth={selectedImageLayerBorderWidth}
                                                                selectedImageLayerBorderType={selectedImageLayerBorderType}

                                                                selectedImageLayerBackgroundColor={selectedImageLayerBackgroundColor}
                                                                selectedImageLayerBackgroundOpacity={selectedImageLayerBackgroundOpacity}

                                                                imageBorderStyleList={imageBorderStyleList}
                                                                imageBorderWidthList={imageBorderWidthList}

                                                                isPickingImageLayerBackgroundColor={isPickingImageLayerBackgroundColor}
                                                                isPickingImageLayerBorderColor={isPickingImageLayerBorderColor}

                                                                onSelectImageLayerImage={(img) => {
                                                                    setSelectedImageLayerImage(img);
                                                                    applySelectedImageLayerSource(img);
                                                                }}
                                                                onSelectImageLayerOpacity={(alpha) => {
                                                                    setSelectedImageLayerOpacity(alpha);
                                                                    applySelectedImageLayerOpacity(alpha);
                                                                }}

                                                                onSelectImageLayerBorderColor={(color) => {
                                                                    setSelectedImageLayerBorderColor(color);
                                                                    applySelectedImageLayerBorderColor(color);
                                                                }}
                                                                onSelectImageLayerBackgroundColor={(color) => {
                                                                    setSelectedImageLayerBackgroundColor(color);
                                                                    applySelectedImageLayerBackgroundColor(color);
                                                                }}

                                                                onChangeImageLayerBorderOpacity={(alpha) => {
                                                                    setSelectedImageLayerBorderOpacity(alpha);
                                                                    applySelectedImageLayerBorderOpacity(alpha);
                                                                }}
                                                                onChangeImageLayerBackgroundOpacity={(alpha) => {
                                                                    setSelectedImageLayerBackgroundOpacity(alpha);
                                                                    applySelectedImageLayerBackgroundOpacity(alpha);
                                                                }}

                                                                onSelectImageLayerBorderWidth={(width) => {
                                                                    setSelectedImageLayerBorderWidth(width);
                                                                    applySelectedImageLayerBorderWidth(width);
                                                                }}
                                                                onSelectImageLayerBorderType={(type) => {
                                                                    setSelectedImageLayerBorderType(type);
                                                                    setSelectedImageLayerBorderStyle(type as "none" | "solid" | "dashed" | "dotted");
                                                                }}

                                                                canUndoSelectedLayer={canUndoSelectedLayer}
                                                                canRedoSelectedLayer={canRedoSelectedLayer}
                                                                onUndoSelectedLayer={undoSelectedLayerContentChange}
                                                                onRedoSelectedLayer={redoSelectedLayerContentChange}
                                                            />
                                                        </div>
                                                    : <></>
                                                }
                                            </>
                                            
                                        }
                                    />
                                    { selectedImage || hoverPreview 
                                        ?   <ContextMenu
                                                wrapperRef={canvasContextAreaRef}
                                                contextMenu={contextMenu}
                                                setContextMenu={setContextMenu}

                                                baseImage={selectedImage}
                                                selectedLayer={selectedLayer}
                                                selectedLayerIDs={selectedLayerIDs}
                                                layerareaSelection={layerAreaSelection}
                                                workOnBaseImage={workOnBaseImage}

                                                allLayer={imageLayer}

                                                canUndoLayer={canUndoSelectedLayer}
                                                canRedoLayer={canRedoSelectedLayer}

                                                onResetBaseImage={handleReset}
                                                onUndoBaseImage={handleUndo}
                                                onRedoBaseImage={handleRedo}
                                                onRotateBaseImage={(degrees) => openRotate(degrees, undefined, "base")}
                                                onFlipVerticalBaseImage={flipCanvasVertical}
                                                onFlipHorizontalBaseImage={flipCanvasHorizontal}
                                                onCropBaseImage={openCropCanvas}
                                                onWriteAllLayerToBaseImage={() => handleWriteLayerOnBaseImage('all')}
                                                onZoomIn={handleZoomIn}
                                                onZoomOut={handleZoomOut}
                                                onExportImage={handleExportImage}
                                                onExpandBaseTransparent={openExpandBaseTransparent}
                                                onExpandBaseWithVariations={() => openExpandWithVariations("base")}
                                                onExpandLayerTransparent={openExpandLayerTransparent}
                                                onExpandLayerWithVariations={() => openExpandWithVariations("layer")}

                                                onDeleteSelectedAreaFromLayer={deleteSelectedAreaFromLayer}
                                                onDuplicateSelectedAreaFromLayer={copySelectedAreaFromLayer}
                                                onExtractSelectedAreaToNewLayer={extractSelectedAreaToNewLayer}
                                                onChangeMode={setSafeToolkitMode}
                                                selectionDisabled={selectedLayerIDs.length > 1 || selectedLayer?.type === "text"}
                                                
                                                onDeleteSelectedLayer={() => dispatchToolkitAction("delete-selected-layers")}
                                                onDuplicateSelectedLayer={duplicateSelectedLayer}
                                                onRotateSelectedLayer={(degrees) => {
                                                    const layerId =
                                                        selectedLayer?.layerID ??
                                                        (selectedLayerIDs.length === 1 ? selectedLayerIDs[0] : undefined);

                                                    if (layerId == null) return;

                                                    openRotate(degrees, layerId, "layer")
                                                }}
                                                onCropSelectedLayerSave={() => dispatchToolkitAction("crop-selected-layer")}
                                                onUndoSelectedLayerContentChange={undoSelectedLayerContentChange}
                                                onRedoSelectedLayerContentChange={redoSelectedLayerContentChange}
                                                onFlipHorizontalSelectedLayer={() => dispatchToolkitAction("flip-horizontal-layer")}
                                                onFlipVerticalSelectedLayer={() => dispatchToolkitAction("flip-vertical-layer")}
                                                onWriteSelectedLayerToBaseImage={() => handleWriteLayerOnBaseImage('selected')}
                                                onMergeSelectedLayer={() => dispatchToolkitAction("merge-all-selected-layers")}
                                                onMergeLayerWithPrevious={() => dispatchToolkitAction("merge-selected-with-previous")}
                                                onHideSelectedLayer={handleHideSelectedLayer}
                                                onChangeLayerToBack={onChangeLayerToBack}
                                                onChangeLayerToFront={onChangeLayerToFront}

                                                onAddLayer={(item) => handleAddLayer(item)}
                                                upscaleCosts={imageEditCosts ? {
                                                    upscale2x: imageEditCosts.upscale2x ?? 0,
                                                    upscale4x: imageEditCosts.upscale4x ?? 0
                                                } : null}

                                                onRemoveBackground={(target: 'base' | 'layer') => handleGenerateVariations('removeBackground', target)}
                                                onRemoveObject={(target: 'base' | 'layer') => handleGenerateVariations('removeObject', target)}
                                                onExtractObject={(target: 'base' | 'layer') => handleGenerateVariations('extractObject', target)}
                                                onConvertToImageLayer={(target: 'base' | 'layer') => handleGenerateVariations('convertToImage', target)}
                                                onUpscale={(target: 'base' | 'layer') => handleGenerateVariations('imageUpscale-2x', target)}
                                                onCreateVariations={(target: 'base' | 'layer') => handleGenerateVariations('generateVariationsImage', target)}
                                                onExtractPose={(target: 'base' | 'layer') => handleGenerateVariations('extractPose', target)}
                                                onSpellCorrection={(target: 'base' | 'layer') => handleGenerateVariations('spellCorrection', target)}
                                            /> 
                                        :   <></>
                                    }
                                </div>
                                {/* <div className='row px-2 my-2'>
                                    { generatedImages.length > 1
                                        ?   <div className='col-auto align-items-start'>
                                                <EffectThemeImageGrid 
                                                    size={'50px'} 
                                                    images={generatedImages} 
                                                    onImageHover={(index:number,item: ImageProps) => hoverPreviewBaseImage('generation',index,item)}
                                                    onImageHoverEnd={clearHoverPreview}
                                                    onImageSelect={handleSelectImage}
                                                />
                                            </div>
                                            
                                        :   <></>
                                    }
                                </div> */}
                                <div className='row px-2'>
                                    <div className="mb-2">
                                        <button
                                            className="btn btn-transparent m-0 p-0 btn-lg"
                                            onClick={() => setShowGallery(!showGallery)}
                                        >
                                            <FontAwesomeIcon
                                                icon={showGallery ? faChevronDown : faChevronRight}
                                                className="me-2"
                                            />
                                            {t('page.gallery')}
                                        </button>
                                    </div>
                                </div>
                                <ImageStudioGallery
                                    show={showGallery}
                                    baseImage={selectedImage}
                                    folderActive={folderActive}
                                    currentImages={currentImages}
                                    currentImageObjects={currentImageObjects}
                                    historyOfUsedImages={historyOfUsedImages}
                                    allImages={allImages}
                                    onFolderChange={setFolderActive}
                                    onImageHover={(index:number, item: ImageProps) => hoverPreviewBaseImage('gallery',index,item)}
                                    onImageHoverEnd={clearHoverPreview}
                                    onImageSelect={handleSelectImage}
                                    onChangeHistoryOfUsedImages={setHistoryOfUsedImages}
                                    onChangeAllImages={setAllImages}
                                    onChangeCurrentImages={setCurrentImages}
                                    onChangeCurrentImagesObject={setCurrentImageObjects}
                                    History_Cookie_name={History_Cookie_name}
                                    MAIN_DATA_PATH_IMAGES={MAIN_DATA_PATH_IMAGES}
                                    onUploadImage={createNewImageEntry}
                                    clearProject={clearLayerSidebarItems}
                                    onGalleryImageDragStart={handleGalleryImageDragStart}
                                    onAddGalleryImageAsLayer={handleAddGalleryImageAsLayer}
                                />
                            </div>
                        </div>  
                    </div>
                    <div className="col-2 my-2 h-100 pe-2 pb-2 d-flex flex-column overflow-hidden">
                        <div className='row m-0 p-0'>
                            <div className='col'></div>
                            <div className='col-auto m-0 p-0 mb-1 mt-auto'>
                                <InfoPopoverButton
                                    width={620}
                                    maxHeight={650}
                                    placement='left'
                                    children={<ControllInfoElement/>}
                                    image={sengiControll1}   
                                    imageHeight={55}
                                    imageBorder={false}
                                />
                            </div>
                            <div className='col-auto m-0 p-0 mb-1'>
                                <InfoPopoverButton
                                    width={820}
                                    maxHeight={650}
                                    placement='left'
                                    children={<WorkLoadInfoElement/>}
                                    image={sengiQuestionRight}   
                                    imageHeight={70}
                                    imageBorder={false}
                                />
                            </div>
                        </div>
                        <div
                            className="row m-0 p-0 flex-grow-1 overflow-hidden"
                            style={{ minHeight: 0 }}
                        >
                            {layerSideBar}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>
  );
};

export default ImageStudio;